import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { api, patchKatNode, flattenKatTree, stylySpizirny } from '../lib/spizirnaUi';
import {
    UtensilsCrossed, Search, Plus, Edit3, Trash2, X, Loader2, ChevronUp,
    ChevronDown, Save, Package, FlaskConical, ShoppingCart, ClipboardList, PlusCircle, Check,
    FolderTree, GripVertical, ChevronRight, FolderPlus, Star, Clock, Settings2, Copy,
    Image, Link2, Sparkles, Scale, Archive, ArchiveRestore,
} from 'lucide-react';
import NahratMedia from '../components/NahratMedia';
import { presunKategorii, posunKategorii } from '../components/receptura/poradiKategorii';
import RecepturaEditor from '../components/receptura/RecepturaEditor';
import RecipeListPanel from '../components/receptura/RecipeListPanel';
import ModulLayout from '../components/layout/ModulLayout';
import { Z_NAD_LISTOU } from '../components/ui/ModalShell';
import IngredienceListPanel from '../components/receptura/IngredienceListPanel';
import PriplatekCeny from '../components/receptura/PriplatekCeny';
import EshopNahled from '../components/receptura/EshopNahled';
import ComboSkupinySprava from '../components/receptura/ComboSkupinySprava';
import SlozeniProduktu from '../components/receptura/SlozeniProduktu';
import NastaveniPridavku from '../components/receptura/NastaveniPridavku';
import KategorieDetail from '../components/receptura/KategorieDetail';
import KategorieDopad from '../components/receptura/KategorieDopad';
import StaniceProduktu from '../components/receptura/StaniceProduktu';
import UpravyPoTuknuti from '../components/receptura/UpravyPoTuknuti';
import PridavkyVCene from '../components/receptura/PridavkyVCene';
import StitekPolozky, { zdedenyStitek } from '../components/receptura/StitekPolozky';
import RecipeHero from '../components/receptura/RecipeHero';
import AkceMenu from '../components/receptura/AkceMenu';
import Dropdown from '../components/receptura/Dropdown';
import NazevInput from '../components/receptura/NazevInput';
import PotvrzeniModal from '../components/receptura/PotvrzeniModal';
import RecipeKatIcon from '../components/receptura/RecipeKatIcon';
import PouzitiModal from '../components/receptura/PouzitiModal';
import ReceptBaleni from '../components/receptura/ReceptBaleni';
import ReceptPoplatekBaleni from '../components/receptura/ReceptPoplatekBaleni';
import RecipeCalcCard from '../components/receptura/RecipeCalcCard';
import RecipeAlergenyInline from '../components/receptura/RecipeAlergenyInline';
import RecipeValidaceUpozorneni from '../components/receptura/RecipeValidaceUpozorneni';
import IconPickerModal from '../components/receptura/IconPickerModal';
import AiAnalyzaModal from '../components/receptura/AiAnalyzaModal';
import { getKatVisual } from '../components/receptura/receptUtils';
import { PRAH_MARZE, MARZE_BARVY } from '../components/receptura/prehledGrid';
import RecipeReadView from '../components/receptura/RecipeReadView';
import JednotkyModal from '../components/receptura/JednotkyModal';
import PrejmenovatVariantuModal from '../components/receptura/PrejmenovatVariantuModal';
import MapovaniObjedname from './receptar/MapovaniObjedname';
import ZpetTlacitko from '../components/ui/ZpetTlacitko';
import usePotvrzeni from '../components/ui/usePotvrzeni';


/** Totéž pro seznam receptů — vizuál kategorie (název/barva/ikona) nesou vnořeně. */
const patchReceptyKategorie = (recepty, katId, patch) => (recepty || []).map(r =>
    r.kategorie_id === katId && r.kategorie
        ? { ...r, kategorie: { ...r.kategorie, ...patch } }
        : r
);

const SECTION_COLOR = '#F59E0B';
const SECTION_BG = 'rgba(245,158,11,0.12)';

/**
 * Rozsah množství přídavku lidsky.
 *
 * Dvě čísla popisují všechna čtyři chování, která provoz potřebuje, ale
 * „−1 až 3" na kartě nikomu nic neřekne — proto se to překládá.
 */
const popisRozsahuKusu = (min, max) => {
    if (min < 0 && max === 0) return 'jen odebrat';
    if (min < 0 && max > 1) return `lze odebrat, až ${max}×`;
    if (min < 0) return 'lze odebrat';
    if (max > 1) return `až ${max}×`;
    return 'jen zaškrtnout';
};

const TYP_COLORS = {
    MAT: { bg: 'rgba(6,182,212,0.15)', color: '#06B6D4', border: 'rgba(6,182,212,0.3)', label: 'MAT' },
    SEM: { bg: 'rgba(249,115,22,0.15)', color: '#F97316', border: 'rgba(249,115,22,0.3)', label: 'SEM' },
    PRO: { bg: 'rgba(168,85,247,0.15)', color: '#A855F7', border: 'rgba(168,85,247,0.3)', label: 'PRO' },
    OBA: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', border: 'rgba(16,185,129,0.3)', label: 'OBA' },
    SPOZ: { bg: 'rgba(234,179,8,0.15)', color: '#EAB308', border: 'rgba(234,179,8,0.3)', label: 'SPOZ' },
    ADD: { bg: 'rgba(236,72,153,0.15)', color: '#EC4899', border: 'rgba(236,72,153,0.3)', label: 'ADD' },
};

const fmtDate = (v) => { if (!v) return ''; const d = new Date(v); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; };

const TypBadge = ({ typ }) => {
    const c = TYP_COLORS[typ] || TYP_COLORS.MAT;
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>{c.label}</span>;
};


const TABS = [
    { id: 'polotovary', label: 'Polotovary', Icon: FlaskConical },
    { id: 'produkty', label: 'Produkty', Icon: ShoppingCart },
    { id: 'pridavky', label: 'Ingredience', Icon: PlusCircle },
    // Dočasná záložka — žije do doby, než nahradíme Objedname vlastním frontendem
    { id: 'objedname', label: 'Mapování Objedname', Icon: Link2, adminOnly: true },
];

// Kanály odběru — hodnoty sedí s stg_obj_objednavky.kanal
const KANAL_NAZVY = { bistro: 'Bistro', rozvoz: 'Rozvoz', vydej: 'Výdej' };

export default function ProduktKvalita({
    user, onNavigate, initialPolozka = null,
    // Vložený do modulu Receptáře: záložku i hlavičku řídí obal. Bez těch
    // propů si stránka vystačí sama, takže jde pořád otevřít samostatně.
    tab: tabZvenku = null, onTab = null, vlozena = false,
    // Místo pro Palec (mobil) — jen Ingredience jedou na mobilní normě
    // (`mobilRezim`), takže si ho dávají dovnitř scrollujících ploch samy.
    rezervaDole = null,
}) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    // Potvrzení nevratné akce přes ModalShell místo window.confirm (norma §1g)
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const [tabVlastni, setTabVlastni] = useState('polotovary');
    const tab = tabZvenku ?? tabVlastni;
    const setTab = onTab ?? setTabVlastni;
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [isWide, setIsWide] = useState(() => window.innerWidth >= 1280);
    const isAdmin = ['superadmin', 'admin'].includes(user?.role);

    useEffect(() => {
        const handler = () => { setIsMobile(window.innerWidth < 768); setIsWide(window.innerWidth >= 1280); };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    const {
        secondaryColor, mutedColor, cardStyle, glassCard, inputStyle, selectStyle,
        optionStyle, btnPrimary, btnSecondary, thStyle, tdStyle, renderFormField,
    } = stylySpizirny(isDark);

    // Mléčné sklo pro modaly (neprůhledné pozadí + rozostření)

    // ─── Shared data (ciselniky) ─────────────────────────────
    const [jednotky, setJednotky] = useState([]);
    const [kategorie, setKategorie] = useState([]);
    const [dphSazby, setDphSazby] = useState([]);

    useEffect(() => {
        api('/api/ciselniky/jednotky').then(setJednotky).catch(() => {});
        api('/api/ciselniky/kategorie-surovin').then(setKategorie).catch(() => {});
        api('/api/ciselniky/dph-sazby').then(setDphSazby).catch(() => {});
    }, []);

    // ─── Read-only data for BOM (suroviny, obaly, spotřební zboží) ──
    const [suroviny, setSuroviny] = useState([]);
    const [obaly, setObaly] = useState([]);
    const [spotrebniZbozi, setSpotrebniZbozi] = useState([]);

    const loadSuroviny = useCallback(async () => {
        try { setSuroviny(await api('/api/recepty/suroviny')); } catch {}
    }, []);

    const loadObaly = useCallback(async () => {
        try { setObaly(await api('/api/recepty/obaly')); } catch {}
    }, []);

    const loadSpotrebniZbozi = useCallback(async () => {
        try { setSpotrebniZbozi(await api('/api/recepty/spotrebni-zbozi')); } catch {}
    }, []);

    // ─── Tab: Recepty ───────────────────────────────────────
    const [recepty, setRecepty] = useState([]);
    const [recSearch, setRecSearch] = useState('');
    const [recTypFilter, setRecTypFilter] = useState('PRO');
    const [recKatFilter, setRecKatFilter] = useState('');
    const [recActiveOnly, setRecActiveOnly] = useState(false); // neaktivni polozky jdou do archivu ve stromu
    const [prodejMap, setProdejMap] = useState(null);          // { recept_id: { celkem, dny[] } } pro sloupec prodeje
    // Potvrzení mazání — { typ: 'recept'|'pridavek', id, nazev }; nativní confirm() nahrazen modalem
    const [smazatModal, setSmazatModal] = useState(null);
    const [smazatBusy, setSmazatBusy] = useState(false);
    const [prejmenovatVariantu, setPrejmenovatVariantu] = useState(false);
    const [recLoading, setRecLoading] = useState(false);
    const [recSaving, setRecSaving] = useState(false);
    const [recError, setRecError] = useState('');
    const [eshopMediaModal, setEshopMediaModal] = useState(false);
    // Fotka pro e-shop je zároveň fotka položky — ať produkt nemá dvě různé.
    // Ikona se ukládá hned (PATCH), jako u výběru ikony; detail se schválně
    // nepřenačítá, smazalo by to rozdělané neuložené změny.
    // Fotka pro e-shop se ukládá hned (PATCH), ne až přes Uložit — jinak po
    // reloadu zbyla jen ikona, protože ta se ukládala okamžitě a e-shopová
    // vazba čekala v rozdělaném formuláři. Zároveň se sjednotí s ikonou
    // položky, ať produkt nemá dvě různé fotky.
    // Detail schválně nepřenačítáme, smazalo by to ostatní neuložené změny.
    const ulozEshopFotku = (soubor) => {
        if (!soubor?.id || !recDetail || recDetail === 'new') return;
        const cesta = soubor.thumbnail_cesta || soubor.cesta;
        const patch = { eshop_media_soubor_id: soubor.id };
        if (cesta) { patch.foto_url = `/storage/${cesta}`; patch.ikona = null; }

        api(`/api/recepty/recepty/${recDetail}`, 'PATCH', patch)
            .then((odpoved) => {
                setRecDetailData(prev => (prev ? { ...prev, ...patch, eshop_media: soubor } : prev));
                loadRecepty();
                const varianty = odpoved?.foto_doplnena_variantam?.length || 0;
                if (varianty) {
                    window.dispatchEvent(new CustomEvent('in-app-toast', {
                        detail: {
                            id: crypto.randomUUID(),
                            title: 'Fotka doplněna',
                            body: `Stejnou fotku dostaly i další varianty (${varianty}), které žádnou neměly.`,
                        },
                    }));
                }
            })
            .catch(e => alert(e.error || 'Fotku se nepodařilo uložit'));
    };

    const [eshopMediaList, setEshopMediaList] = useState([]);
    const [eshopMediaSearch, setEshopMediaSearch] = useState('');
    const [eshopSelectedMedia, setEshopSelectedMedia] = useState(null);
    const [eshopVarianty, setEshopVarianty] = useState([]);
    const [recDetail, setRecDetail] = useState(null);
    // Otevřená kategorie (id) — detail kategorie místo detailu receptu (standard §1i).
    const [katDetail, setKatDetail] = useState(null);
    // Zvýšení přenačte ingredience v detailu kategorie i sloupec Dopad.
    const [katPridavkyKlic, setKatPridavkyKlic] = useState(0);
    const [recDetailData, setRecDetailData] = useState(null);
    const [recEditData, setRecEditData] = useState(null);
    // Neuložené změny v editoru receptury (kroky/dávka) → hlásí RecepturaEditor.
    // Ukládají se společně s polí receptu jedním tlačítkem Uložit nahoře na položce.
    const [receptEditorDirty, setReceptEditorDirty] = useState(false);
    const [topSaving, setTopSaving] = useState(false);
    const receptEditorSaveRef = useRef(null); // saveAll z RecepturaEditor
    const [recKatVarianty, setRecKatVarianty] = useState([]);
    const recKatIdForVarianty = recEditData?.kategorie_id;
    useEffect(() => {
        if (recKatIdForVarianty) {
            api(`/api/recepty/kategorie/${recKatIdForVarianty}/efektivni-varianty`).then(d => setRecKatVarianty(d || [])).catch(() => setRecKatVarianty([]));
        } else {
            setRecKatVarianty([]);
        }
    }, [recKatIdForVarianty]);
    const [recDetailTab, setRecDetailTab] = useState('info');
    const [recCeny, setRecCeny] = useState([]);
    const [recCenaForm, setRecCenaForm] = useState(null);
    const [recCenaSaving, setRecCenaSaving] = useState(false);
    const [recAltJednotky, setRecAltJednotky] = useState([]);
    const [recKatStromSEM, setRecKatStromSEM] = useState([]);
    const [recKatStromPRO, setRecKatStromPRO] = useState([]);
    const [bomData, setBomData] = useState([]);
    const [kalkulace, setKalkulace] = useState(null);
    const [calcLoading, setCalcLoading] = useState(false);
    const [recRefreshTick, setRecRefreshTick] = useState(0); // signál pro realtime přepočet (alergeny apod.)
    const calcTimerRef = useRef(null);
    // Debounced přepočet kalkulace — real-time při editaci receptury
    const refreshKalkulace = useCallback(() => {
        if (!recDetail || recDetail === 'new') return;
        setCalcLoading(true);
        if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
        calcTimerRef.current = setTimeout(() => {
            api(`/api/recepty/recepty/${recDetail}/kalkulace`)
                .then(setKalkulace).catch(() => {})
                .finally(() => { setCalcLoading(false); setRecRefreshTick(t => t + 1); });
        }, 350);
    }, [recDetail]);
    // Proklik zvenčí — ingredience typu SEM v jiném receptu, nebo řádek ze
    // seznamu použití (ten může ukázat i na produkt či přídavek).
    const TAB_PRO_TYP_RECEPTU = { SEM: 'polotovary', PRO: 'produkty', ADD: 'pridavky' };

    useEffect(() => {
        const cilovyTab = TAB_PRO_TYP_RECEPTU[initialPolozka?.typ];
        if (!cilovyTab || !initialPolozka?.id) return;
        setTab(cilovyTab);
        if (initialPolozka.typ === 'ADD') loadAddDetail(initialPolozka.id);
        else loadRecDetail(initialPolozka.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPolozka?.typ, initialPolozka?.id]);

    // Kde se položka používá (+ hromadná náhrada) — { typ, id } | null
    const [pouzitiModal, setPouzitiModal] = useState(null);
    const [katModalOpen, setKatModalOpen] = useState(false);
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    // AI analýza kategorie — { id, nazev } | null
    const [aiAnalyzaKat, setAiAnalyzaKat] = useState(null);
    const [revHistModalOpen, setRevHistModalOpen] = useState(false);
    const [saveRevModalOpen, setSaveRevModalOpen] = useState(false);
    const [jednotkyModalOpen, setJednotkyModalOpen] = useState(false);
    const [copyBomModal, setCopyBomModal] = useState(false);
    const [copyBomSearch, setCopyBomSearch] = useState('');
    const [copyBomSourceId, setCopyBomSourceId] = useState(null);
    const [copyBomLoading, setCopyBomLoading] = useState(false);
    const [revize, setRevize] = useState([]);
    const [revizePoznamka, setRevizePoznamka] = useState('');
    // Změna, se kterou se musí seznámit obsluha (potvrdí ji v Cockpitu → Dokumentace).
    // Drobnosti (překlep, fotka) potvrzení nespouští — proto je to volba, ne pravidlo.
    const [revizeSeznameni, setRevizeSeznameni] = useState(false);
    const [revizeSaving, setRevizeSaving] = useState(false);
    const [revizeAllLoaded, setRevizeAllLoaded] = useState(false);

    // ─── Návrh textu revize dle změn ────────────────────────
    // Baseline = snímek receptu při načtení; při otevření revize se porovná s aktuálním stavem.
    const revizeBaselineRef = useRef(null);
    const recSelectRef = useRef(null); // poslední vyžádané id detailu — zahodí doběhlé fetche přepnutých položek

    const bomSignature = (kusovnik) => (kusovnik || []).map(i => ({
        key: i.surovina_id ? `s${i.surovina_id}`
            : i.obal_id ? `o${i.obal_id}`
            : i.spotrebni_zbozi_id ? `z${i.spotrebni_zbozi_id}`
            : i.ingredience_recept_id ? `r${i.ingredience_recept_id}`
            : `x${i.id}`,
        nazev: i.surovina?.nazev || i.obal?.nazev || i.spotrebni_zbozi?.nazev || i.ingredience_recept?.nazev || 'položka',
        spotreba: Number(i.spotreba) || 0,
        jed: i.jednotka?.zkratka || '',
    }));

    const krokSignature = (kroky) => (kroky || []).map(k => ({
        // `??`, ne `||`: změna z jednoho operátora na nulu je změna kroku
        // a musí se v porovnání revizí projevit.
        id: k.id, nazev: k.nazev || '', cas_s: k.cas_s ?? 0, ops: k.pocet_operatoru ?? 1,
        stroj: (k.stroje?.map(s => s.id) ?? (k.stroj_id ? [k.stroj_id] : [])).slice().sort().join(','),
    }));

    const buildReceptSnapshot = (d) => ({
        nazev: d?.nazev || '',
        kategorie_id: d?.kategorie_id ?? null,
        jednotka_id: d?.jednotka_id ?? null,
        pocet_porci: d?.pocet_porci ?? null,
        varianta: d?.varianta || '',
        aktivni: !!d?.aktivni,
        bom: bomSignature(d?.kusovnik),
        kroky: krokSignature(d?.sop?.kroky),
    });

    // Porovná baseline vs aktuální snímek a složí lidský návrh textu revize.
    const buildRevizeNavrh = (base, cur) => {
        if (!base || !cur) return '';
        const parts = [];
        const cislo = (v) => String(v ?? '').replace('.', ',');

        if (base.nazev !== cur.nazev && cur.nazev) parts.push(`přejmenováno na „${cur.nazev}"`);
        if ((base.kategorie_id ?? null) !== (cur.kategorie_id ?? null)) {
            const kat = kategorie.find(k => k.id === cur.kategorie_id);
            parts.push(cur.kategorie_id ? `přeřazeno do kategorie „${kat?.nazev || '?'}"` : 'odebrána kategorie');
        }
        if ((base.jednotka_id ?? null) !== (cur.jednotka_id ?? null)) {
            const j = jednotky.find(u => u.id === cur.jednotka_id);
            parts.push(`změněna základní jednotka na ${j?.zkratka || '?'}`);
        }
        if (Number(base.pocet_porci) !== Number(cur.pocet_porci)) {
            const j = jednotky.find(u => u.id === cur.jednotka_id);
            parts.push(`upravena velikost dávky na ${cislo(cur.pocet_porci)}${j?.zkratka ? ' ' + j.zkratka : ''}`);
        }
        if ((base.varianta || '') !== (cur.varianta || '')) {
            parts.push(cur.varianta ? `varianta „${cur.varianta}"` : 'odebrána varianta');
        }
        if (base.aktivni !== cur.aktivni) parts.push(cur.aktivni ? 'položka obnovena z archivu' : 'položka archivována');

        // Ingredience
        const baseMap = new Map((base.bom || []).map(b => [b.key, b]));
        const curMap = new Map((cur.bom || []).map(b => [b.key, b]));
        const pridano = (cur.bom || []).filter(b => !baseMap.has(b.key)).map(b => b.nazev);
        const odebrano = (base.bom || []).filter(b => !curMap.has(b.key)).map(b => b.nazev);
        const zmeneno = (cur.bom || []).filter(b => baseMap.has(b.key) && Math.abs(baseMap.get(b.key).spotreba - b.spotreba) > 1e-9).map(b => b.nazev);
        if (pridano.length) parts.push(`přidáno: ${pridano.join(', ')}`);
        if (odebrano.length) parts.push(`odebráno: ${odebrano.join(', ')}`);
        if (zmeneno.length) parts.push(`upraveno množství: ${zmeneno.join(', ')}`);

        // Postupové kroky
        const baseIds = new Set((base.kroky || []).map(k => k.id));
        const curIds = new Set((cur.kroky || []).map(k => k.id));
        const krokAdd = (cur.kroky || []).filter(k => !baseIds.has(k.id)).length;
        const krokDel = (base.kroky || []).filter(k => !curIds.has(k.id)).length;
        const krokChanged = (cur.kroky || []).some(k => {
            if (!baseIds.has(k.id)) return false;
            const bk = (base.kroky || []).find(x => x.id === k.id);
            return bk && (bk.nazev !== k.nazev || bk.cas_s !== k.cas_s || bk.ops !== k.ops || bk.stroj !== k.stroj);
        });
        if (krokAdd) parts.push(`přidán postupový krok${krokAdd > 1 ? ` (${krokAdd}×)` : ''}`);
        if (krokDel) parts.push(`odebrán krok${krokDel > 1 ? ` (${krokDel}×)` : ''}`);
        if (krokChanged) parts.push('upraven postup');

        if (parts.length === 0) return '';
        const text = parts.join('; ');
        return text.charAt(0).toUpperCase() + text.slice(1) + '.';
    };

    // Při otevření revize (modal / historie) předvyplní návrh dle změn.
    useEffect(() => {
        if (!(saveRevModalOpen || revHistModalOpen)) return;
        if (!recDetail || recDetail === 'new' || !revizeBaselineRef.current) return;
        let cancelled = false;
        (async () => {
            try {
                const d2 = await api(`/api/recepty/recepty/${recDetail}`);
                const cur = buildReceptSnapshot(d2);
                if (recEditData) {
                    // Základní pole mohou být rozeditovaná (ještě neuložená) — vezmi z formuláře.
                    cur.nazev = recEditData.nazev ?? cur.nazev;
                    cur.kategorie_id = recEditData.kategorie_id || null;
                    cur.jednotka_id = recEditData.jednotka_id || null;
                    cur.pocet_porci = recEditData.pocet_porci ?? cur.pocet_porci;
                    cur.varianta = recEditData.varianta || '';
                    cur.aktivni = !!recEditData.aktivni;
                }
                const navrh = buildRevizeNavrh(revizeBaselineRef.current, cur);
                if (!cancelled) setRevizePoznamka(prev => (prev && prev.trim()) ? prev : (navrh || 'Kontrola bez úprav.'));
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [saveRevModalOpen, revHistModalOpen]);

    // ─── Tab: Přídavky ──────────────────────────────────────
    const [addRecepty, setAddRecepty] = useState([]);
    const [addSearch, setAddSearch] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [addDetail, setAddDetail] = useState(null);
    const [addDetailData, setAddDetailData] = useState(null);
    const [addEditData, setAddEditData] = useState(null);
    const [addDetailTab, setAddDetailTab] = useState('receptura');
    const [addSkupiny, setAddSkupiny] = useState([]);
    const [addSaving, setAddSaving] = useState(false);
    const [addTopSaving, setAddTopSaving] = useState(false);
    const [addError, setAddError] = useState('');
    const [addCeny, setAddCeny] = useState([]);
    const [addCenaForm, setAddCenaForm] = useState(null);
    const [addCenaSaving, setAddCenaSaving] = useState(false);
    const [addKalkulace, setAddKalkulace] = useState(null);
    const [addCalcLoading, setAddCalcLoading] = useState(false);
    const [addRefreshTick, setAddRefreshTick] = useState(0); // signál pro přepočet alergenů
    const [addRevize, setAddRevize] = useState([]);
    const [addRevizePoznamka, setAddRevizePoznamka] = useState('');
    const [addRevizeAllLoaded, setAddRevizeAllLoaded] = useState(false);
    const [addSaveRevModalOpen, setAddSaveRevModalOpen] = useState(false);
    const [addIconPickerOpen, setAddIconPickerOpen] = useState(false);
    const [addSkupinyPickerOpen, setAddSkupinyPickerOpen] = useState(false);
    const [addAltJednotky, setAddAltJednotky] = useState([]);
    const [addJednotkyOpen, setAddJednotkyOpen] = useState(false);
    // Pořadí řádků, jak je vidí člověk v seznamu (pohled, filtr, sbalené
    // skupiny) — podle něj jezdí šipky ↑↓ v detailu.
    const addPoradiRef = useRef([]);
    const nastavAddPoradi = useCallback((ids) => { addPoradiRef.current = ids; }, []);
    // Editor receptury přídavku je stejný jako u produktu — drží si vlastní draft,
    // ukládá se přes hlavní tlačítko Uložit (saveRef) a hlásí neuložené změny.
    const [addEditorDirty, setAddEditorDirty] = useState(false);
    const addEditorSaveRef = useRef(null);
    const addRevizeBaselineRef = useRef(null);
    const addSelectRef = useRef(null);
    const addCalcTimerRef = useRef(null);
    // PRO přídavky sub-tab
    // Zvýšení přenačte skupiny ingrediencí (rozsah závisí na složení).
    const [pridavkyKlic, setPridavkyKlic] = useState(0);
    const [addSkupinyModalOpen, setAddSkupinyModalOpen] = useState(false);
    const [addSkupinyForm, setAddSkupinyForm] = useState(null);
    const [addSkupinySaving, setAddSkupinySaving] = useState(false);
    // Po úpravě combo skupin (ceny, položky) se záložka Combo načte znovu.
    const [comboKlic, setComboKlic] = useState(0);

    // Resolve active price from ceny array
    const resolveAktivniCena = (ceny) => {
        if (!ceny || ceny.length === 0) return null;
        const today = new Date().toISOString().slice(0, 10);
        return ceny.find(c => {
            const od = c.platnost_od ? (typeof c.platnost_od === 'string' ? c.platnost_od.slice(0, 10) : c.platnost_od) : null;
            const doo = c.platnost_do ? (typeof c.platnost_do === 'string' ? c.platnost_do.slice(0, 10) : c.platnost_do) : null;
            return od && od <= today && (!doo || doo >= today);
        }) || null;
    };

    // Parse BOM items from API response
    const parseBomItems = (items) => items.map(i => {
        const src = i.surovina || i.obal || i.spotrebni_zbozi || null;
        const semRecept = i.ingredience_recept || null;
        return {
            id: crypto.randomUUID(),
            _serverId: i.id,
            surovina_id: i.surovina_id, ingredience_recept_id: i.ingredience_recept_id, obal_id: i.obal_id, spotrebni_zbozi_id: i.spotrebni_zbozi_id,
            spotreba: i.spotreba, jednotka_id: i.jednotka_id, scrap_pct: i.scrap_pct || 0,
            sop_krok_id: i.sop_krok_id || null,
            _label: i.surovina ? i.surovina.nazev : i.obal ? i.obal.nazev : i.spotrebni_zbozi ? i.spotrebni_zbozi.nazev : semRecept?.nazev || '?',
            _kod: i.surovina ? i.surovina.kod : i.obal ? i.obal.kod : i.spotrebni_zbozi ? i.spotrebni_zbozi.kod : semRecept?.kod || '',
            _typ: i.surovina_id ? 'MAT' : i.obal_id ? 'OBA' : i.spotrebni_zbozi_id ? 'SPOZ' : 'SEM',
            _primaryJednotkaId: src?.jednotka_id || semRecept?.jednotka_id || i.jednotka_id,
            _altJednotky: src?.alt_jednotky || semRecept?.alt_jednotky || [],
            _aktivniCena: i.surovina_id ? resolveAktivniCena(i.surovina?.ceny) : i.obal_id ? resolveAktivniCena(i.obal?.ceny) : i.spotrebni_zbozi_id ? resolveAktivniCena(i.spotrebni_zbozi?.ceny) : resolveAktivniCena(semRecept?.ceny),
            _jednotkaZkratka: i.jednotka?.zkratka || '',
        };
    });

    // silent = bez skeletonu; seznam zůstane na místě a jen se přepíše, až data
    // dorazí. Endpoint je těžký (stovky receptů), takže problikávat skeletonem
    // kvůli změně na pozadí je zbytečně rušivé.
    const loadRecepty = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setRecLoading(true);
        try { setRecepty(await api('/api/recepty/recepty')); } catch {}
        if (!silent) setRecLoading(false);
    }, []);

    // ─── Přídavky loaders ───────────────────────────────────
    // Seznam bere stejný endpoint jako produkty (jen ?typ=ADD) — díky tomu má
    // řádek přídavku identickou výbavu: cenu na jednotku, validační semafor,
    // počet ingrediencí. `/recepty/pridavky` zůstává pro pokladnu a e-shop.
    const loadAddRecepty = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setAddLoading(true);
        try { setAddRecepty(await api('/api/recepty/recepty?typ=ADD')); } catch {}
        if (!silent) setAddLoading(false);
    }, []);

    const loadAddSkupiny = useCallback(async () => {
        try { setAddSkupiny(await api('/api/recepty/pridavky-skupiny')); } catch {}
    }, []);

    const loadAddDetail = async (id, { keepTab = false } = {}) => {
        addSelectRef.current = id; // označ nejnovější výběr (doběhlé fetche zahoď)
        setAddDetail(id); setAddSkupinyPickerOpen(false);
        // Na mobilu je první záložka Karta — hlavička tam pole nenese (§1e).
        if (!keepTab) { setAddDetailTab(isMobile ? 'karta' : 'receptura'); setAddCeny([]); setAddCenaForm(null); setAddRevize([]); setAddRevizeAllLoaded(false); setAddRevizePoznamka(''); }
        setAddError('');
        try {
            const d = await api(`/api/recepty/pridavky/${id}`);
            if (addSelectRef.current !== id) return;
            setAddDetailData(d);
            addRevizeBaselineRef.current = buildReceptSnapshot(d);
            setAddEditData({
                nazev: d.nazev, aktivni: d.aktivni, poznamka: d.poznamka || '',
                jednotka_id: d.jednotka_id || '',
                skupina_ids: (d.pridavky_skupiny || []).map(s => s.id),
            });
            setAddCeny(d.ceny || []);
            setAddRevize(d.revize || []);
            setAddAltJednotky(d.alt_jednotky || []);
        } catch { if (addSelectRef.current === id) setAddDetailData(null); }
    };

    // Leave-guard nad editorem receptury přídavku (obdoba guardReceptura u receptů).
    // Asynchronní — ptá se přes ModalShell, ne přes window.confirm (§1g).
    const guardAddReceptura = async () => {
        if (!addEditorDirty) return true;
        const ok = await potvrdit('Opustit bez uložení?', {
            text: 'V receptuře máš neuložené změny. Když odejdeš, ztratí se.', potvrdit: 'Zahodit změny',
        });
        if (ok) setAddEditorDirty(false);
        return ok;
    };

    const zavritAddDetail = () => { setAddDetail(null); setAddDetailData(null); setAddEditData(null); };

    const novaIngredience = async () => {
        if (!(await guardAddReceptura())) return;
        setAddDetail('new'); setAddDetailData(null);
        setAddEditData({ nazev: '', aktivni: true, poznamka: '', jednotka_id: '', skupina_ids: [] });
        setAddDetailTab(isMobile ? 'karta' : 'info'); setAddError('');
        setAddCeny([]); setAddCenaForm(null); setAddRevize([]); setAddAltJednotky([]);
    };

    // Příplatek: uložení (zadává se s DPH, bez DPH dopočítá server) a smazání.
    const ulozAddCenu = async () => {
        setAddCenaSaving(true);
        try {
            // Starou cenu bez DPH neposílat, jinak by úprava nic nezměnila.
            const { cena_bez_dph, ...data } = addCenaForm;
            if (addCenaForm.id) await api(`/api/recepty/ceny-receptu/${addCenaForm.id}`, 'PUT', data);
            else await api(`/api/recepty/recepty/${addDetail}/ceny`, 'POST', data);
            setAddCenaForm(null);
            setAddCeny(await api(`/api/recepty/recepty/${addDetail}/ceny`));
            refreshAddKalkulace();           // marže se počítá z ceny
            loadAddRecepty({ silent: true }); // sloupec příplatku a marže v seznamu
        } catch (e) { alert(e.error || 'Chyba'); }
        setAddCenaSaving(false);
    };
    const smazAddCenu = async (c) => {
        if (!(await potvrdit('Smazat příplatek?', { text: `${parseFloat(c.cena_s_dph).toFixed(2)} Kč s DPH, platnost od ${fmtDate(c.platnost_od)}.`, potvrdit: 'Smazat' }))) return;
        try {
            await api(`/api/recepty/ceny-receptu/${c.id}`, 'DELETE');
            setAddCeny(await api(`/api/recepty/recepty/${addDetail}/ceny`));
            refreshAddKalkulace();
            loadAddRecepty({ silent: true });
        } catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    // Payload hlavičky přídavku — prázdná jednotka musí jít na server jako null,
    // ne jako "" (jinak neprojde pravidlem `exists`).
    const addPayload = () => ({
        ...addEditData,
        jednotka_id: addEditData?.jednotka_id || null,
    });

    const saveAddRecept = async () => {
        if (!addEditData?.nazev?.trim()) { alert('Zadejte název.'); return; }
        setAddSaving(true); setAddError('');
        try {
            if (addDetail === 'new') {
                const created = await api('/api/recepty/pridavky', 'POST', addPayload());
                loadAddRecepty();
                loadAddDetail(created.id);
            } else {
                await api(`/api/recepty/pridavky/${addDetail}`, 'PUT', addPayload());
                loadAddRecepty();
                loadAddDetail(addDetail, { keepTab: true });
                refreshAddKalkulace();
            }
        } catch (e) { setAddError(e.message || e.error || 'Chyba'); }
        setAddSaving(false);
    };

    // Uložení hlavičky + zápis revize (z modalu po stisku Uložit) — jako u receptů.
    const saveAddWithRevision = async () => {
        if (!addEditData?.nazev?.trim()) { setAddError('Zadejte název.'); return; }
        setAddSaving(true); setAddError('');
        try {
            await api(`/api/recepty/pridavky/${addDetail}`, 'PUT', addPayload());
            const rv = await api(`/api/recepty/recepty/${addDetail}/revize`, 'POST', { poznamka: addRevizePoznamka.trim() });
            setAddRevize(prev => [rv, ...prev]);
            setAddSaveRevModalOpen(false);
            setAddRevizePoznamka('');
            loadAddRecepty();
            loadAddDetail(addDetail, { keepTab: true });
            refreshAddKalkulace();
        } catch (e) { setAddError(e.error || e.message || 'Chyba'); }
        setAddSaving(false);
    };

    const deleteAddRecept = async (id) => {
        try { await api(`/api/recepty/pridavky/${id}`, 'DELETE'); loadAddRecepty(); setAddDetail(null); setAddDetailData(null); }
        catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    /** Potvrzené mazání z modalu — zavře ho až po doběhnutí akce. */
    const potvrditSmazani = async () => {
        if (!smazatModal) return;
        setSmazatBusy(true);
        try {
            if (smazatModal.typ === 'recept') await deleteRecept(smazatModal.id);
            else await deleteAddRecept(smazatModal.id);
            setSmazatModal(null);
        } finally { setSmazatBusy(false); }
    };

    // Debouncovaný přepočet kalkulace přídavku — realtime při editaci receptury.
    const refreshAddKalkulace = useCallback(() => {
        if (!addDetail || addDetail === 'new') return;
        setAddCalcLoading(true);
        if (addCalcTimerRef.current) clearTimeout(addCalcTimerRef.current);
        addCalcTimerRef.current = setTimeout(() => {
            api(`/api/recepty/recepty/${addDetail}/kalkulace`)
                .then(setAddKalkulace).catch(() => {})
                .finally(() => { setAddCalcLoading(false); setAddRefreshTick(t => t + 1); });
        }, 350);
    }, [addDetail]);



    const loadRecDetail = async (id, { keepTab = false } = {}) => {
        recSelectRef.current = id; // označ nejnovější výběr
        setRecDetail(id); if (!keepTab) { setRecDetailTab('receptura'); setBomData([]); setRecCeny([]); setRecCenaForm(null); setRevize([]); setRevizeAllLoaded(false); setRevizePoznamka(''); } setRecError('');
        try {
            const d = await api(`/api/recepty/recepty/${id}`);
            if (recSelectRef.current !== id) return; // mezitím přepnuto na jinou položku → zahodit
            setRecDetailData(d);
            revizeBaselineRef.current = buildReceptSnapshot(d); // snímek pro návrh textu revize
            setRecEditData({ nazev: d.nazev, typ: d.typ, kod: d.kod, kategorie_id: d.kategorie_id || '', varianta: d.varianta || '', aktivni: d.aktivni, poznamka: d.poznamka || '', sop_id: d.sop_id || '', pocet_porci: d.pocet_porci || 1, jednotka_id: d.jednotka_id || '', eshop_viditelny: d.eshop_viditelny || false, eshop_popis: d.eshop_popis || '', eshop_media_soubor_id: d.eshop_media_soubor_id || null, eshop_primarni: d.eshop_primarni || false, pos_vychozi: d.pos_vychozi || false, kds_stanice_id: d.kds_stanice_id ?? null, pos_upravy: d.pos_upravy ?? null, stitek: d.stitek ?? null, stitek_skupina: d.stitek_skupina ?? null, pridavky_v_cene: d.pridavky_v_cene ?? null });
            setRecCeny(d.ceny || []);
            setRecAltJednotky(d.alt_jednotky || []);
            setRevize(d.revize || []);
            setEshopSelectedMedia(d.eshop_media || null);
            api(`/api/recepty/recepty/${id}/varianty`).then(v => { if (recSelectRef.current === id) setEshopVarianty(v); }).catch(() => { if (recSelectRef.current === id) setEshopVarianty([]); });
            // Load BOM
            const items = await api(`/api/recepty/recepty/${id}/kusovnik`);
            if (recSelectRef.current !== id) return;
            setBomData(parseBomItems(items));
        } catch { if (recSelectRef.current === id) setRecDetailData(null); }
    };

    // Leave-guard: když jsou v editoru receptury neuložené změny, potvrď odchod.
    // Vrací true = pokračovat (uživatel potvrdil / nic neuloženého není).
    const guardReceptura = () => {
        if (!receptEditorDirty) return true;
        const ok = window.confirm('V receptuře máš neuložené změny. Opustit bez uložení? Změny se ztratí.');
        if (ok) setReceptEditorDirty(false);
        return ok;
    };

    const saveRecept = async () => {
        if (!recEditData?.nazev?.trim()) { alert('Zadejte název.'); return; }
        if (!recEditData?.jednotka_id) { alert('Vyberte základní jednotku.'); setJednotkyModalOpen(true); return; }
        setRecSaving(true); setRecError('');
        try {
            if (recDetail === 'new') {
                const created = await api('/api/recepty/recepty', 'POST', recEditData);
                loadRecepty();
                loadRecDetail(created.id);
            } else {
                await api(`/api/recepty/recepty/${recDetail}`, 'PUT', recEditData);
                loadRecepty();
                loadRecDetail(recDetail, { keepTab: true });
                refreshKalkulace();
            }
        } catch (e) { setRecError(e.message || e.error || 'Chyba'); }
        setRecSaving(false);
    };

    // Uložení změn + zápis revize (z modalu po stisku Uložit)
    const saveReceptWithRevision = async () => {
        if (!recEditData?.nazev?.trim()) { setRecError('Zadejte název.'); return; }
        if (!recEditData?.jednotka_id) { setRecError('Vyberte základní jednotku.'); return; }
        setRecSaving(true); setRecError('');
        try {
            await api(`/api/recepty/recepty/${recDetail}`, 'PUT', recEditData);
            const poznamka = revizePoznamka.trim();
            const rv = await api(`/api/recepty/recepty/${recDetail}/revize`, 'POST', { poznamka, vyzaduje_seznameni: revizeSeznameni });
            setRevize(prev => [rv, ...prev]);
            setSaveRevModalOpen(false);
            setRevizePoznamka('');
            setRevizeSeznameni(false);
            loadRecepty();
            loadRecDetail(recDetail, { keepTab: true });
            refreshKalkulace();
        } catch (e) { setRecError(e.error || e.message || 'Chyba'); }
        setRecSaving(false);
    };

    // Vytvoření nové varianty — zkopíruje recepturu (BOM + SOP) i sek. jednotky ze základní položky
    const createVariant = async () => {
        if (!recDetailData) return;
        try {
            const src = recDetailData;
            const newName = `Varianta ${(eshopVarianty.length || 1) + 1}`;
            const created = await api('/api/recepty/recepty', 'POST', {
                nazev: src.nazev, typ: src.typ, kategorie_id: src.kategorie_id || null,
                varianta: newName, aktivni: true, poznamka: src.poznamka || '',
                pocet_porci: src.pocet_porci || 1, jednotka_id: src.jednotka_id || null,
            });
            for (const aj of recAltJednotky) {
                await api(`/api/recepty/recepty/${created.id}/alt-jednotky`, 'POST', { jednotka_do_id: aj.jednotka_do_id || aj.jednotka_do?.id, koeficient: aj.koeficient }).catch(() => {});
            }
            await api(`/api/recepty/recepty/${created.id}/kusovnik/copy-from`, 'POST', { source_id: src.id }).catch(() => {});
            loadRecepty();
            loadRecDetail(created.id);
        } catch (e) { alert(e.error || 'Chyba při vytváření varianty'); }
    };

    // Přejmenování varianty — ukládá se hned (PATCH), proto se nový název propíše
    // i do výchozího stavu formuláře a snímku revize, ať nesvítí jako neuložená změna.
    const renameVariant = async (varianta) => {
        const id = recDetail;
        const d = await api(`/api/recepty/recepty/${id}`, 'PATCH', { varianta: varianta || null });
        if (recSelectRef.current !== id) return;
        setRecDetailData(prev => prev ? { ...prev, varianta: d.varianta } : prev);
        setRecEditData(prev => prev ? { ...prev, varianta: d.varianta || '' } : prev);
        if (revizeBaselineRef.current) revizeBaselineRef.current = { ...revizeBaselineRef.current, varianta: d.varianta || '' };
        setEshopVarianty(prev => prev.map(v => v.id === id ? { ...v, varianta: d.varianta } : v));
        loadRecepty();
    };

    const deleteRecept = async (id) => {
        try { await api(`/api/recepty/recepty/${id}`, 'DELETE'); loadRecepty(); setRecDetail(null); setRecDetailData(null); }
        catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    const copyBomFrom = async (sourceId) => {
        setCopyBomLoading(true);
        try {
            const result = await api(`/api/recepty/recepty/${recDetail}/kusovnik/copy-from`, 'POST', { source_id: sourceId });
            // Odpověď obsahuje recept (s novými SOP kroky) + kusovník
            if (result.recept) {
                setRecDetailData(result.recept);
                setRecEditData(prev => prev ? { ...prev, sop_id: result.recept.sop_id || prev.sop_id } : prev);
            }
            setBomData(parseBomItems(result.kusovnik || result));
            setCopyBomModal(false);
            setCopyBomSourceId(null);
            setCopyBomSearch('');
            api(`/api/recepty/recepty/${recDetail}/kalkulace`).then(setKalkulace).catch(() => {});
        } catch (e) { alert(e.error || 'Chyba při kopírování receptury'); }
        setCopyBomLoading(false);
    };

    const saveRecCena = async () => {
        setRecCenaSaving(true);
        try {
            // Zadává se cena s DPH, bez DPH si dopočítá server (2026-10-23).
            const { cena_bez_dph, ...data } = recCenaForm;
            if (recCenaForm.id) {
                await api(`/api/recepty/ceny-receptu/${recCenaForm.id}`, 'PUT', data);
            } else {
                await api(`/api/recepty/recepty/${recDetail}/ceny`, 'POST', data);
            }
            setRecCenaForm(null);
            setRecCeny(await api(`/api/recepty/recepty/${recDetail}/ceny`));
            refreshKalkulace();   // marže se počítá z ceny, musí se přepočítat
        } catch (e) { alert(e.error || 'Chyba'); }
        setRecCenaSaving(false);
    };

    const deleteRecCena = async (id) => {
        if (!confirm('Smazat cenu?')) return;
        try {
            await api(`/api/recepty/ceny-receptu/${id}`, 'DELETE');
            setRecCeny(await api(`/api/recepty/recepty/${recDetail}/ceny`));
        } catch {}
    };

    // Kalkulace (pro hero i tab) — vždy při otevření/změně detailu
    useEffect(() => {
        if (recDetail && recDetail !== 'new') {
            api(`/api/recepty/recepty/${recDetail}/kalkulace`).then(setKalkulace).catch(() => setKalkulace(null));
        } else {
            setKalkulace(null);
        }
    }, [recDetail]);

    // Refresh SOP kroky + kalkulace when switching to relevant tabs (po úpravách receptury)
    useEffect(() => {
        if ((recDetailTab === 'receptura' || recDetailTab === 'info' || recDetailTab === 'kalkulace') && recDetail && recDetail !== 'new') {
            let alive = true; // pojistka: přepnutí položky zahodí doběhlý fetch té staré
            if (recDetailTab === 'receptura') {
                api(`/api/recepty/recepty/${recDetail}`).then(d => {
                    if (!alive) return;
                    setRecDetailData(d);
                    setRecEditData(prev => prev ? { ...prev, sop_id: d.sop_id || '' } : prev);
                }).catch(() => {});
            }
            api(`/api/recepty/recepty/${recDetail}/kalkulace`).then(d => { if (alive) setKalkulace(d); }).catch(() => { if (alive) setKalkulace(null); });
            return () => { alive = false; };
        }
    }, [recDetailTab, recDetail]);

    // Load full revision history when opening the revize modal
    useEffect(() => {
        if (revHistModalOpen && recDetail && recDetail !== 'new' && !revizeAllLoaded) {
            api(`/api/recepty/recepty/${recDetail}/revize`).then(d => { setRevize(d); setRevizeAllLoaded(true); }).catch(() => {});
        }
    }, [revHistModalOpen, recDetail, revizeAllLoaded]);

    // Kalkulace přídavku (hero, stálá karta) — vždy při otevření/změně detailu
    useEffect(() => {
        if (addDetail && addDetail !== 'new') {
            api(`/api/recepty/recepty/${addDetail}/kalkulace`).then(setAddKalkulace).catch(() => setAddKalkulace(null));
        } else {
            setAddKalkulace(null);
        }
    }, [addDetail]);

    // Karta je jen mobilní záložka — po roztažení okna by zůstal prázdný detail.
    useEffect(() => {
        if (!isMobile && addDetailTab === 'karta') setAddDetailTab('receptura');
    }, [isMobile, addDetailTab]);

    // Lazy loading pro ADD detail sub-taby
    useEffect(() => {
        if (addDetailTab === 'revize' && addDetail && addDetail !== 'new' && !addRevizeAllLoaded) {
            api(`/api/recepty/recepty/${addDetail}/revize`).then(d => { setAddRevize(d); setAddRevizeAllLoaded(true); }).catch(() => {});
        }
    }, [addDetailTab, addDetail, addRevizeAllLoaded]);

    // Návrh textu revize přídavku dle provedených změn (stejně jako u receptů)
    useEffect(() => {
        if (!addSaveRevModalOpen || !addDetail || addDetail === 'new' || !addRevizeBaselineRef.current) return;
        let cancelled = false;
        (async () => {
            try {
                const d2 = await api(`/api/recepty/recepty/${addDetail}`);
                const cur = buildReceptSnapshot(d2);
                if (addEditData) {
                    cur.nazev = addEditData.nazev ?? cur.nazev;
                    cur.jednotka_id = addEditData.jednotka_id || null;
                    cur.aktivni = !!addEditData.aktivni;
                }
                const navrh = buildRevizeNavrh(addRevizeBaselineRef.current, cur);
                if (!cancelled) setAddRevizePoznamka(prev => (prev && prev.trim()) ? prev : (navrh || 'Kontrola bez úprav.'));
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [addSaveRevModalOpen]);


    // Přehled combo skupin (patička seznamu produktů); po zavření se záložka
    // Combo načte znovu — mohly se změnit položky a ceny.
    const [comboSpravaOpen, setComboSpravaOpen] = useState(false);
    useEffect(() => {
        if (!comboSpravaOpen) setComboKlic(k => k + 1);
    }, [comboSpravaOpen]);

    // ─── Krok edit modal ─────────────────────────────────────

    // ─── Stroje (data pro dropdowny) ────────────────────────
    const [stroje, setStroje] = useState([]);

    const loadStroje = useCallback(async () => {
        try { setStroje(await api('/api/recepty/stroje')); } catch {}
    }, []);

    // lazy tab loading
    useEffect(() => {
        if (tab === 'polotovary' || tab === 'produkty') {
            setRecTypFilter(tab === 'polotovary' ? 'SEM' : 'PRO');
            setRecDetail(null); setRecDetailData(null); setRecKatFilter('');
            if (recepty.length === 0) loadRecepty();
            // Prodej za 30 dní jede zvlášť — je to jeden agregát a seznam
            // receptur na něj nemusí čekat, sloupec se doplní, až dorazí.
            if (!prodejMap) api('/api/recepty/prodej?dni=30').then(d => setProdejMap(d?.polozky || {})).catch(() => setProdejMap({}));
            if (suroviny.length === 0) loadSuroviny();
            if (obaly.length === 0) loadObaly();
            if (spotrebniZbozi.length === 0) loadSpotrebniZbozi();
            api('/api/recepty/kategorie-strom?typ=SEM').then(setRecKatStromSEM).catch(() => {});
            api('/api/recepty/kategorie-strom?typ=PRO').then(setRecKatStromPRO).catch(() => {});
        }
        if ((tab === 'polotovary' || tab === 'produkty') && stroje.length === 0) loadStroje();
        if (tab === 'pridavky') {
            setAddDetail(null); setAddDetailData(null);
            if (addRecepty.length === 0) loadAddRecepty();
            if (addSkupiny.length === 0) loadAddSkupiny();
            // Ingredience si editor receptury hledá vlastním endpointem, stačí stroje.
            if (stroje.length === 0) loadStroje();
        }
    }, [tab]);

    // Bez argumentu načte oba stromy (první načtení stránky). S typem jen ten
    // dotčený — úprava kategorie polotovarů nemá důvod sahat na strom produktů.
    const reloadKatStrom = useCallback((typ = null) => {
        if (typ !== 'PRO') api('/api/recepty/kategorie-strom?typ=SEM').then(setRecKatStromSEM).catch(() => {});
        if (typ !== 'SEM') api('/api/recepty/kategorie-strom?typ=PRO').then(setRecKatStromPRO).catch(() => {});
    }, []);

    // ─── Helpers ────────────────────────────────────────────

    const EmptyState = ({ text }) => (
        <div className="rounded-xl p-8 text-center" style={cardStyle}>
            <p className="text-sm" style={{ color: mutedColor }}>{text}</p>
        </div>
    );

    // Detekce neuložených změn (dirty) hlavičkových polí.
    // Boolean pole porovnáváme přes Boolean() — z API chodí true/false, ale
    // ?? '' by u `false` vedle prázdné hodnoty hlásilo změnu pořád dokola.
    const REC_BOOL_POLE = { aktivni: true, eshop_viditelny: false, eshop_primarni: false, pos_vychozi: false };
    const recFieldDirty = (key) => {
        if (recDetail === 'new' || !recDetailData || !recEditData) return false;
        if (key in REC_BOOL_POLE) {
            const vychozi = REC_BOOL_POLE[key];
            return Boolean(recEditData[key] ?? vychozi) !== Boolean(recDetailData[key] ?? vychozi);
        }
        return (recEditData[key] ?? '') !== (recDetailData[key] ?? '');
    };
    // E-shopová pole tu dřív chyběla, takže jejich změna nenabídla Uložit —
    // primární varianta ani výchozí pro pokladnu tím pádem nešly zaškrtnout.
    const recDirty = recDetail === 'new'
        ? true
        : ['nazev', 'kategorie_id', 'varianta', 'jednotka_id', 'aktivni', 'poznamka',
           'eshop_viditelny', 'eshop_popis', 'eshop_primarni', 'pos_vychozi', 'kds_stanice_id', 'pos_upravy', 'stitek', 'stitek_skupina', 'pridavky_v_cene'].some(recFieldDirty);
    const dirtyRing = (on) => on ? { boxShadow: '0 0 0 2px rgba(245,158,11,0.55)' } : {};

    // Totéž pro hlavičku přídavku
    const addFieldDirty = (key) => {
        if (addDetail === 'new' || !addDetailData || !addEditData) return false;
        if (key === 'aktivni') return (addEditData.aktivni ?? true) !== (addDetailData.aktivni ?? true);
        if (key === 'skupina_ids') {
            const a = [...(addEditData.skupina_ids || [])].sort().join(',');
            const b = (addDetailData.pridavky_skupiny || []).map(s => s.id).sort().join(',');
            return a !== b;
        }
        return (addEditData[key] ?? '') !== (addDetailData[key] ?? '');
    };
    const addDirty = addDetail === 'new'
        ? true
        : ['nazev', 'jednotka_id', 'aktivni', 'poznamka', 'skupina_ids'].some(addFieldDirty);

    // Řádek "nastavení": label vlevo, ovládací prvek vpravo (užší sloupec)
    const infoRow = (label, control, { hint, alignTop, last } = {}) => (
        <div className="flex gap-3 py-2.5" style={{ borderBottom: last ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, alignItems: alignTop ? 'flex-start' : 'center' }}>
            <div style={{ width: 128, flexShrink: 0, paddingTop: alignTop ? 6 : 0 }}>
                <span className="text-xs font-medium" style={{ color: secondaryColor }}>{label}</span>
                {hint && <p className="text-[10px] mt-0.5" style={{ color: '#EF4444' }}>{hint}</p>}
            </div>
            <div className="flex-1 min-w-0" style={{ maxWidth: 340 }}>{control}</div>
        </div>
    );

    // ─── Tree helpers ────────────────────────────────────────
    const getDescendantIds = (nodes, targetId) => {
        const ids = [];
        const collect = (items) => { for (const item of items) { ids.push(item.id); if (item.children?.length) collect(item.children); } };
        const find = (items) => { for (const item of items) { if (item.id == targetId) { ids.push(item.id); collect(item.children || []); return true; } if (item.children?.length && find(item.children)) return true; } return false; };
        find(nodes);
        return ids;
    };

    // ─── RENDER ─────────────────────────────────────────────
    const flatRecKatSEM = useMemo(() => flattenKatTree(recKatStromSEM), [recKatStromSEM]);
    const flatRecKatPRO = useMemo(() => flattenKatTree(recKatStromPRO), [recKatStromPRO]);
    const recKatStromActive = recTypFilter === 'SEM' ? recKatStromSEM : recKatStromPRO;
    const flatRecKat = recTypFilter === 'SEM' ? flatRecKatSEM : flatRecKatPRO;

    // ── Kategorie: úprava ze stromu i z detailu kategorie ──
    // Optimisticky: strom i vizuál kategorie v seznamu se přepíšou hned,
    // PUT běží na pozadí. Nic se nepřenačítá — seznam receptů je drahý
    // (stovky položek) a na změnu názvu/barvy/ikony ho není proč tahat.
    const ulozKategorii = (id, patch) => {
        const typ = recTypFilter;
        const setStrom = typ === 'SEM' ? setRecKatStromSEM : setRecKatStromPRO;
        setStrom(prev => patchKatNode(prev, id, patch));
        setRecepty(prev => patchReceptyKategorie(prev, id, patch));
        setRecDetailData(prev => (prev?.kategorie_id === id && prev.kategorie
            ? { ...prev, kategorie: { ...prev.kategorie, ...patch } } : prev));
        api(`/api/recepty/kategorie-surovin/${id}`, 'PUT', patch)
            // Obrázek pro e-shop server ořízne — náhled ukáže až jeho výsledek.
            .then(k => {
                if (!k) return;
                if ('eshop_media_soubor_id' in patch) setStrom(prev => patchKatNode(prev, id, { eshop_foto: k.eshop_foto_cesta ? `/storage/${k.eshop_foto_cesta}` : null, eshop_foto_vyrez: !!k.eshop_foto_vyrez }));
                if ('eshop_zahlavi_media_soubor_id' in patch) setStrom(prev => patchKatNode(prev, id, { eshop_zahlavi: k.eshop_zahlavi_cesta ? `/storage/${k.eshop_zahlavi_cesta}` : null }));
            })
            .catch(e => { alert(e.error || 'Chyba'); reloadKatStrom(typ); loadRecepty({ silent: true }); });
    };
    // Pořadí kategorií (společné pro pokladnu, kiosek i e-shop): přetažení
    // nad/pod jinou kategorii ve stromu, šipky v detailu kategorie.
    const presunKat = (srcId, cilId, poloha) => presunKategorii(recKatStromActive, srcId, cilId, poloha)
        .then(() => reloadKatStrom(recTypFilter)).catch(e => alert(e.error || 'Přeřazení kategorie se nezdařilo'));
    const posunKat = (id, smer) => posunKategorii(recKatStromActive, id, smer)
        .then(() => reloadKatStrom(recTypFilter)).catch(e => alert(e.error || 'Přeřazení kategorie se nezdařilo'));
    const pridejKategorii = (parentId, data) => api('/api/recepty/kategorie-surovin', 'POST', { typ: recTypFilter, parent_id: parentId, aktivni: true, ...data })
        .then(() => reloadKatStrom(recTypFilter)).catch(e => alert(e.error || 'Chyba'));
    const smazKategorii = (id) => api(`/api/recepty/kategorie-surovin/${id}`, 'DELETE')
        .then(() => { if (katDetail === id) setKatDetail(null); reloadKatStrom(recTypFilter); loadRecepty({ silent: true }); })
        .catch(e => alert(e.error || 'Chyba'));

    // Otevřená kategorie se čte ze stromu — optimistické úpravy se tak
    // promítnou do detailu hned. Po přepnutí polotovary ↔ produkty zmizí sama.
    const katDetailNode = katDetail ? flatRecKat.find(k => k.id === katDetail) || null : null;
    const katDetailPocty = useMemo(() => {
        if (!katDetailNode) return { produktu: 0, velikosti: 0, podkategorii: 0 };
        const ids = new Set();
        const projdi = (n) => { ids.add(n.id); (n.children || []).forEach(projdi); };
        projdi(katDetailNode);
        const aktivni = recepty.filter(r => r.aktivni !== false && ids.has(r.kategorie_id));
        return {
            // Stejný klíč jako recVariantKey (definovaný níž) — varianty jsou jeden produkt.
            produktu: new Set(aktivni.map(r => `${r.kategorie_id || '_'}::${r.nazev}`)).size,
            velikosti: aktivni.length,
            podkategorii: ids.size - 1,
        };
    }, [katDetailNode, recepty]);

    const recVariantKey = (r) => `${r.kategorie_id || '_'}::${r.nazev}`;
    const recGrouped = useMemo(() => {
        const map = new Map();
        recepty.filter(r => r.typ === recTypFilter).forEach(r => {
            const key = recVariantKey(r);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return map;
    }, [recepty, recTypFilter]);

    const filteredRec = useMemo(() => {
        let result = recepty.filter(r => {
            if (recActiveOnly && !r.aktivni) return false;
            if (recTypFilter && r.typ !== recTypFilter) return false;
            if (recKatFilter) {
                const allowedIds = getDescendantIds(recKatStromActive, recKatFilter);
                if (!allowedIds.includes(r.kategorie_id)) return false;
            }
            if (recSearch) {
                const q = recSearch.toLowerCase();
                return r.nazev?.toLowerCase().includes(q) || r.kod?.toLowerCase().includes(q);
            }
            return true;
        });
        // Seskupit varianty — zobrazit jen jednu za skupinu
        const seen = new Set();
        result = result.filter(r => {
            const key = recVariantKey(r);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        result.sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
        return result;
    }, [recepty, recActiveOnly, recTypFilter, recKatFilter, recSearch, recKatStromActive]);

    // ─── Keyboard navigace šipkami ↑↓ v detailu ────────────
    useEffect(() => {
        const handler = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            e.preventDefault();

            const dir = e.key === 'ArrowUp' ? -1 : 1;
            let nextId = null;

            if ((tab === 'polotovary' || tab === 'produkty') && recDetail && recDetail !== 'new') {
                const idx = filteredRec.findIndex(r => r.id === recDetail);
                const next = idx + dir;
                if (next >= 0 && next < filteredRec.length && guardReceptura()) { nextId = filteredRec[next].id; loadRecDetail(nextId); }
            }

            // Ingredience: pořadí, jak ho vidí člověk v seznamu (viz IngredienceListPanel).
            if (tab === 'pridavky' && addDetail && addDetail !== 'new') {
                const poradi = addPoradiRef.current;
                const next = poradi.indexOf(addDetail) + dir;
                if (next >= 0 && next < poradi.length) {
                    const cil = poradi[next];
                    guardAddReceptura().then(ok => {
                        if (!ok) return;
                        loadAddDetail(cil);
                        setTimeout(() => document.querySelector(`[data-list-id="${cil}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 50);
                    });
                }
            }

            // Scroll aktivní položku do view
            if (nextId) {
                setTimeout(() => {
                    document.querySelector(`[data-list-id="${nextId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }, 50);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [tab, recDetail, filteredRec, addDetail, addEditorDirty]);

    // Lock parent <main> scroll — this page manages its own scroll areas
    useEffect(() => {
        const main = document.querySelector('main');
        if (!main) return;
        const prev = main.style.overflow;
        main.style.overflow = 'hidden';
        return () => { main.style.overflow = prev; };
    }, []);

    // Stránka vyplní obal, ať je vložená nebo samostatná. Dřív si samostatný
    // režim počítal výšku z viewportu (`100vh - 100px`) — pevný odhad odečtu
    // nechával dole mezeru. `main` má díky flex-1 definitivní výšku, takže
    // `100%` sedí vždycky a nezestárne s výškou hlavičky.
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme.
                Ve vloženém režimu ho kreslí obal, jinak by byly dva. */}
            {!isMobile && !vlozena && (
                <div className="flex items-center gap-3 mb-4" style={{ flexShrink: 0 }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SECTION_BG }}>
                        <UtensilsCrossed size={20} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Receptář</h1>
                        <p className="text-xs" style={{ color: secondaryColor }}>
                            {recepty.length} receptů
                        </p>
                    </div>
                </div>
            )}

            {/* Tab bar — ve vloženém režimu ho nese obal se všemi sedmi záložkami */}
            {!vlozena && (
            <div className="flex gap-1 mb-4" style={{
                flexShrink: 0,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}>
                {TABS.filter(t => !t.adminOnly || isAdmin).map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className="flex items-center px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap"
                        style={{ color: tab === t.id ? SECTION_COLOR : secondaryColor, background: 'none', border: 'none',
                            borderBottom: `2px solid ${tab === t.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', flexShrink: 0 }}>
                        <t.Icon size={14} style={{ marginRight: 6, opacity: tab === t.id ? 1 : 0.6 }} />
                        {isMobile ? t.label.split(' ')[0] : t.label}
                    </button>
                ))}
            </div>
            )}

            {/* ═══════ TAB: RECEPTY ═══════ */}
            {(tab === 'polotovary' || tab === 'produkty') && (
                /* Standardní rozvržení modulu — viz components/layout/ModulLayout.
                   Bez výběru je seznam přehled přes celou šířku; po výběru se
                   zúží, vedle je detail a stálý panel (kalkulace + revize). */
                <ModulLayout
                    isMobile={isMobile}
                    vybrano={!!recDetail || !!katDetailNode}
                    seznam={({ siroky }) => (
                        <RecipeListPanel siroky={siroky}
                            onSpravovatComba={() => setComboSpravaOpen(true)}
                            onAiAnalyza={(kat) => setAiAnalyzaKat({ id: kat.id, nazev: kat.nazev })}
                            prodejMap={prodejMap}
                            onKategorieKlik={() => {
                                if (katDetailNode) { setKatDetail(null); return 'prehled'; }
                                if (!recDetail) return 'nic';                 // už jsme v přehledu → obyčejný toggle
                                if (!guardReceptura()) return 'blokovano';    // neuložené změny drží detail otevřený
                                setRecDetail(null); setRecDetailData(null); setRecEditData(null);
                                return 'prehled';
                            }}
                            onUpravitKategorii={(node) => {
                                if (!guardReceptura()) return;
                                setRecDetail(null); setRecDetailData(null); setRecEditData(null);
                                setKatDetail(node.id);
                            }}
                            aktivniKategorieId={katDetailNode?.id ?? null}
                            typ={recTypFilter} isMobile={isMobile} isDark={isDark}
                            mutedColor={mutedColor} secondaryColor={secondaryColor}
                            cardStyle={cardStyle} inputStyle={inputStyle}
                            recSearch={recSearch} setRecSearch={setRecSearch}
                            katStrom={recKatStromActive} filteredRec={filteredRec} recGrouped={recGrouped}
                            recLoading={recLoading}
                            activeKey={recDetail && recDetailData ? recVariantKey(recDetailData) : null}
                            activeId={recDetail}
                            onSelect={(id) => { if (guardReceptura()) { setKatDetail(null); loadRecDetail(id); } }}
                            onNew={() => { setKatDetail(null); setRecDetail('new'); setRecDetailData(null); setRecEditData({ nazev: '', typ: recTypFilter, kategorie_id: '', varianta: '', aktivni: true, poznamka: '', sop_id: '', pocet_porci: 1, jednotka_id: '' }); setRecDetailTab('receptura'); setRecError(''); setBomData([]); setRecCeny([]); setRecCenaForm(null); }}
                            onCatSave={ulozKategorii}
                            onCatAdd={pridejKategorii}
                            onCatDelete={smazKategorii}
                            onMoveItems={(itemIds, catId) => {
                                // Řádek v seznamu zastupuje CELOU skupinu variant (klíč = kategorie
                                // + název), takže přetažením se musí přesunout všechny — jinak
                                // skupina zůstane roztržená mezi dvě kategorie a v obou vypadá
                                // stejně (varianta se v řádku nepíše) = zdánlivý duplikát.
                                // Přetažených řádků může být víc (výběr přes Ctrl).
                                const klice = new Set();
                                for (const itemId of itemIds) {
                                    const zdroj = recepty.find(r => r.id === itemId);
                                    if (zdroj) klice.add(recVariantKey(zdroj));
                                }
                                if (klice.size === 0) return;
                                const ids = recepty.filter(r => klice.has(recVariantKey(r)) && r.kategorie_id !== catId).map(r => r.id);
                                if (ids.length === 0) return; // všechno už v cílové kategorii je

                                // Optimisticky — řádek skočí hned, server jen potvrdí.
                                const k = flatRecKat.find(x => x.id === catId);
                                const kat = k ? { id: k.id, nazev: k.nazev, barva: k.barva, ikona: k.ikona } : null;
                                setRecepty(prev => prev.map(r => (ids.includes(r.id)
                                    ? { ...r, kategorie_id: catId, kategorie: kat } : r)));

                                Promise.all(ids.map(id => api(`/api/recepty/recepty/${id}`, 'PATCH', { kategorie_id: catId })))
                                    .then(() => { if (ids.includes(recDetail)) loadRecDetail(recDetail, { keepTab: true }); })
                                    .catch(e => { alert(e.error || 'Přesun se nezdařil'); loadRecepty({ silent: true }); });
                            }}
                            onReparentCat={(catId, parentId) => api(`/api/recepty/kategorie-surovin/${catId}`, 'PUT', { parent_id: parentId }).then(() => reloadKatStrom(recTypFilter)).catch(e => alert(e.error || 'Přesun kategorie se nezdařil'))}
                            onReorderCat={presunKat}
                        />
                    )}
                    detail={() => katDetailNode ? (
                            <KategorieDetail key={katDetailNode.id} node={katDetailNode} typ={recTypFilter} flatKat={flatRecKat}
                                pocty={katDetailPocty} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} mutedColor={mutedColor}
                                onCatSave={ulozKategorii} onCatAdd={pridejKategorii} onCatDelete={smazKategorii}
                                onPosun={(smer) => posunKat(katDetailNode.id, smer)} strom={recKatStromActive}
                                onAiAnalyza={(kat) => setAiAnalyzaKat({ id: kat.id, nazev: kat.nazev })}
                                onZavrit={() => setKatDetail(null)}
                                pridavkyKlic={katPridavkyKlic} onZmenaPridavku={() => setKatPridavkyKlic(k => k + 1)} />
                    ) : (
                            <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={cardStyle}>
                                {/* Header */}
                                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-start justify-between'} p-4`} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        {/* Jen mobil — na desktopu se do přehledu vrací klikem
                                            na kategorii v seznamu, tlačítko tam bylo navíc. */}
                                        {isMobile && (
                                            <ZpetTlacitko onClick={() => { if (guardReceptura()) { setRecDetail(null); setRecDetailData(null); setRecEditData(null); } }} popis="Zpět na seznam" />
                                        )}
                                        {recDetail !== 'new' && recDetailData && (
                                            <div className="flex flex-col items-center gap-1" style={{ flexShrink: 0 }}>
                                                <button onClick={() => setIconPickerOpen(true)} title="Změnit ikonu / obrázek"
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', position: 'relative' }}
                                                    className="group">
                                                    <RecipeKatIcon foto={recDetailData.foto_url} ikona={recDetailData.ikona || recDetailData.kategorie?.ikona} kategorieNazev={recDetailData.kategorie?.nazev} kategorieBarva={recDetailData.kategorie?.barva} size={58} radius={9} />
                                                    <span className="hidden group-hover:flex" style={{ position: 'absolute', inset: 0, borderRadius: 9, background: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Edit3 size={18} style={{ color: '#fff' }} />
                                                    </span>
                                                </button>
                                                {/* Evidenční kód je databázový údaj, ne titulek — drobným písmem
                                                    pod ikonou, kde se dá přečíst, ale nesoupeří s názvem. */}
                                                <span className="text-[10px] tabular-nums" style={{ color: mutedColor }} title="Evidenční kód položky">{recDetailData.kod}</span>
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            {recEditData ? (() => {
                                                const pill = { background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` };
                                                const bareSelect = { appearance: 'none', WebkitAppearance: 'none', border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', outline: 'none', paddingRight: 2, maxWidth: 160 };
                                                const flatKat = recEditData.typ === 'SEM' ? flatRecKatSEM : flatRecKatPRO;
                                                const kat = flatKat.find(k => k.id === recEditData.kategorie_id);
                                                const katCol = kat?.barva || getKatVisual(kat?.nazev).color;
                                                return (
                                                    <>
                                                        {/* Řádek: název + kód + přepínač variant */}
                                                        <div className="flex items-center gap-2 flex-wrap" style={{ position: 'relative' }}>
                                                            {recEditData && (recEditData.aktivni ?? true) === false && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" title="Položka je v archivu — v nabídce se nezobrazuje"
                                                                    style={{ background: 'rgba(148,163,184,0.16)', color: '#94A3B8', flexShrink: 0 }}>
                                                                    <Archive size={10} /> Archiv
                                                                </span>
                                                            )}
                                                            {/* Šířka podle textu — rostoucí input by výběr varianty odtlačil
                                                                až k tlačítkům a vypadal by, že patří k nim. */}
                                                            <NazevInput value={recEditData.nazev || ''} onChange={e => setRecEditData({ ...recEditData, nazev: e.target.value })}
                                                                placeholder={recTypFilter === 'SEM' ? 'Název polotovaru' : 'Název produktu'}
                                                                style={{ fontSize: 16, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--text-primary)', outline: 'none', padding: '2px 4px', borderRadius: 5, ...dirtyRing(recFieldDirty('nazev')) }} />
                                                            {/* Výběr varianty hned za názvem — jiná verze téže položky,
                                                                ne samostatná sekce.

                                                                Do dvou variant se odznaky vejdou i vedle názvu a přepnutí
                                                                je na jeden klik; víc jich řádek rozbije, tak se sbalí do
                                                                dropdownu. Na úzké obrazovce se sbalí vždycky. Panel kreslí
                                                                stejné ikony a barvy jako seznam vlevo. */}
                                                            {recDetail !== 'new' && eshopVarianty.length > 0 && (() => {
                                                                const jakoOdznaky = isWide && eshopVarianty.length <= 3;
                                                                const prepni = (id) => { if (id !== recDetail && guardReceptura()) loadRecDetail(id, { keepTab: true }); };

                                                                if (jakoOdznaky) {
                                                                    return (
                                                                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                                                                            {eshopVarianty.map(v => {
                                                                                const on = v.id === recDetail;
                                                                                return (
                                                                                    <button key={v.id} onClick={() => prepni(v.id)}
                                                                                        title={v.aktivni ? undefined : 'Varianta je v archivu'}
                                                                                        className="px-2.5 py-1 rounded-full text-[11px] font-medium flex items-center gap-1"
                                                                                        style={{
                                                                                            background: on ? SECTION_COLOR : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                                                                            color: on ? '#fff' : isDark ? 'rgba(255,255,255,0.6)' : 'var(--text-secondary)',
                                                                                            border: 'none', cursor: 'pointer', opacity: v.aktivni ? 1 : 0.5,
                                                                                        }}>
                                                                                        {v.eshop_primarni && <Star size={10} fill={on ? '#fff' : SECTION_COLOR} style={{ flexShrink: 0 }} />}
                                                                                        {/* Pokladna má vlastní výchozí variantu — jiný odznak než hvězda e-shopu */}
                                                                                        {v.pos_vychozi && <ShoppingCart size={10} style={{ flexShrink: 0, color: on ? '#fff' : SECTION_COLOR }} />}
                                                                                        {v.varianta || 'Základní'}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                            <button onClick={createVariant} title="Přidat variantu (zkopíruje recepturu ze základní položky)"
                                                                                className="px-2 py-1 rounded-full text-[11px] font-medium flex items-center gap-1"
                                                                                style={{ background: 'none', border: `1px dashed ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--border-default)'}`, color: secondaryColor, cursor: 'pointer' }}>
                                                                                <Plus size={11} />
                                                                            </button>
                                                                        </span>
                                                                    );
                                                                }

                                                                return (
                                                                    <Dropdown
                                                                        value={recDetail}
                                                                        onChange={(val) => { if (val === '__nova') { createVariant(); return; } prepni(val); }}
                                                                        isDark={isDark} height={28} minWidth={150} accent={SECTION_COLOR}
                                                                        leftIcon={(() => {
                                                                            const v = eshopVarianty.find(x => x.id === recDetail);
                                                                            return v ? <RecipeKatIcon foto={v.foto_url} ikona={v.ikona || v.kategorie?.ikona} kategorieNazev={v.kategorie?.nazev} kategorieBarva={v.kategorie?.barva} size={18} radius={4} /> : null;
                                                                        })()}
                                                                        options={[
                                                                            { value: '__skupina', label: recDetailData?.nazev || 'Varianty', hlavicka: true },
                                                                            ...eshopVarianty.map(v => ({
                                                                                value: v.id,
                                                                                label: v.varianta || 'Základní',
                                                                                popis: v.kod,
                                                                                uroven: 1,
                                                                                tlumene: !v.aktivni,
                                                                                ikona: <RecipeKatIcon foto={v.foto_url} ikona={v.ikona || v.kategorie?.ikona} kategorieNazev={v.kategorie?.nazev} kategorieBarva={v.kategorie?.barva} size={22} radius={5} />,
                                                                                odznaky: (
                                                                                    <span className="inline-flex items-center gap-1" style={{ flexShrink: 0 }}>
                                                                                        {v.eshop_primarni && <Star size={11} fill={SECTION_COLOR} style={{ color: SECTION_COLOR }} />}
                                                                                        {v.pos_vychozi && <ShoppingCart size={11} style={{ color: SECTION_COLOR }} />}
                                                                                    </span>
                                                                                ),
                                                                            })),
                                                                            { value: '__nova', label: 'Nová varianta', uroven: 1, ikona: <Plus size={14} style={{ color: mutedColor }} /> },
                                                                        ]}
                                                                    />
                                                                );
                                                            })()}
                                                        </div>
                                                        {/* Řádek 2: kategorie + jednotka */}
                                                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                                            {/* Kategorie přes náš Dropdown — nativní <select> kreslil OS seznam
                                                                bez ikon i barev a strom v něm byl jen odrážkami „— ". */}
                                                            <div style={{ ...dirtyRing(recFieldDirty('kategorie_id')), borderRadius: 6 }}>
                                                                <Dropdown
                                                                    value={recEditData.kategorie_id || ''}
                                                                    onChange={(val) => setRecEditData({ ...recEditData, kategorie_id: val ? parseInt(val) : null })}
                                                                    isDark={isDark} height={28} minWidth={168} accent={SECTION_COLOR}
                                                                    placeholder="Bez kategorie"
                                                                    leftIcon={<span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: recEditData.kategorie_id ? katCol : 'transparent', border: recEditData.kategorie_id ? 'none' : `1px solid ${mutedColor}` }} />}
                                                                    options={[
                                                                        { value: '', label: 'Bez kategorie', ikona: <span style={{ width: 20, height: 20, borderRadius: 5, border: `1px dashed ${mutedColor}`, display: 'inline-block' }} /> },
                                                                        ...flatKat.map(k => ({
                                                                            value: k.id,
                                                                            label: k.nazev,
                                                                            uroven: k._depth,
                                                                            ikona: <RecipeKatIcon ikona={k.ikona} kategorieNazev={k.nazev} kategorieBarva={k.barva} size={20} radius={5} slozka />,
                                                                        })),
                                                                    ]}
                                                                />
                                                            </div>
                                                            <button onClick={() => setJednotkyModalOpen(true)} title="Nastavit jednotky"
                                                                className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2 pr-2.5" style={{ background: pill.background, border: `1px solid ${!recEditData.jednotka_id ? 'rgba(239,68,68,0.5)' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`, cursor: 'pointer', ...dirtyRing(recFieldDirty('jednotka_id')) }}>
                                                                <Scale size={12} style={{ color: !recEditData.jednotka_id ? '#EF4444' : mutedColor, flexShrink: 0 }} />
                                                                <span className="text-xs font-medium" style={{ color: recEditData.jednotka_id ? 'var(--text-primary)' : '#EF4444' }}>
                                                                    {recEditData.jednotka_id ? (jednotky.find(j => j.id === recEditData.jednotka_id)?.zkratka || 'jedn.') : 'Jednotka *'}
                                                                    {recAltJednotky.length > 0 && <span style={{ color: mutedColor, fontWeight: 400 }}> +{recAltJednotky.length}</span>}
                                                                </span>
                                                            </button>
                                                            {/* Alergeny čísly za jednotkou — druhý řádek na ně má místo
                                                                a název tak zůstane sám na svém. Text až po najetí. */}
                                                            {isWide && recDetail !== 'new' && recDetailData && (
                                                                <RecipeAlergenyInline recept={recDetailData} bomLength={bomData.length} refreshTick={recRefreshTick} />
                                                            )}
                                                            <RecipeValidaceUpozorneni validace={recDetailData?.validace} mutedColor={mutedColor} />
                                                        </div>
                                                    </>
                                                );
                                            })() : (
                                                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{recDetail === 'new' ? 'Nový recept' : '...'}</h3>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2" style={{ flexShrink: 0, ...(isMobile ? { justifyContent: 'flex-end' } : {}) }}>
                                        {recEditData && (() => {
                                            const aktivni = recEditData.aktivni ?? true;
                                            return (
                                                <AkceMenu
                                                    isDark={isDark} mutedColor={mutedColor} glassCard={glassCard}
                                                    polozky={[
                                                        {
                                                            id: 'prejmenovat-variantu',
                                                            label: recDetailData?.varianta ? 'Přejmenovat variantu' : 'Pojmenovat variantu',
                                                            icon: Edit3,
                                                            // I u jediné varianty — produkty vznikají oddělením z jiného
                                                            // a název varianty (např. „32 cm") jim má smysl nechat či dát.
                                                            hidden: recDetail === 'new',
                                                            onClick: () => setPrejmenovatVariantu(true),
                                                        },
                                                        {
                                                            id: 'archiv',
                                                            label: aktivni ? 'Archivovat' : 'Obnovit z archivu',
                                                            icon: aktivni ? Archive : ArchiveRestore,
                                                            onClick: () => setRecEditData({ ...recEditData, aktivni: !aktivni }),
                                                        },
                                                        {
                                                            id: 'smazat',
                                                            label: 'Smazat',
                                                            icon: Trash2,
                                                            danger: true,
                                                            hidden: recDetail === 'new',
                                                            onClick: () => setSmazatModal({ typ: 'recept', id: recDetail, nazev: recDetailData?.nazev || recEditData.nazev }),
                                                        },
                                                    ]}
                                                />
                                            );
                                        })()}
                                        {(() => {
                                            const anyDirty = recDirty || receptEditorDirty;
                                            const busy = recSaving || topSaving;
                                            return (
                                        <button onClick={async () => {
                                            if (!recEditData?.nazev?.trim()) { alert('Zadejte název.'); return; }
                                            if (!recEditData?.jednotka_id) { alert('Vyberte základní jednotku.'); setJednotkyModalOpen(true); return; }
                                            if (recDetail === 'new') { saveRecept(); return; }
                                            // Zachyť dirty editoru PŘED uložením — saveAll ho přes onDirtyChange(false) vynuluje.
                                            const editorWasDirty = receptEditorDirty;
                                            // 1) ulož koncept editoru (kroky/dávka/ingredience)
                                            if (receptEditorDirty && receptEditorSaveRef.current) {
                                                try { setTopSaving(true); await receptEditorSaveRef.current(); } finally { setTopSaving(false); }
                                            }
                                            // 2) revize — otevři i když se měnila jen receptura (ne jen hlavičková pole);
                                            //    poznámka se předvyplní souhrnem změn z baseline snapshotu.
                                            if (recDirty || editorWasDirty) { setRevizePoznamka(''); setSaveRevModalOpen(true); }
                                        }} style={{ ...btnPrimary, padding: '6px 14px', position: 'relative', opacity: (busy || (!anyDirty && recDetail !== 'new')) ? 0.55 : 1 }} disabled={busy || (!anyDirty && recDetail !== 'new')}>
                                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                            {anyDirty && recDetail !== 'new' && (
                                                <span style={{ position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: `2px solid ${isDark ? '#1a1a2e' : '#fff'}` }} />
                                            )}
                                        </button>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Hero — metriky a alergeny (jen když není stálá karta vpravo) */}
                                {recDetail !== 'new' && recDetailData && !isWide && (
                                    <RecipeHero recept={recDetailData} kalkulace={kalkulace} bomLength={bomData.length}
                                        onPouzito={() => setPouzitiModal({ typ: 'SEM', id: recDetailData.id })}
                                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} isMobile={isMobile} />
                                )}

                                {/* Sub-tabs */}
                                <div className="flex gap-1 px-4 pt-3" style={{
                                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                    overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
                                }}>
                                    {[{ id: 'receptura', label: 'Receptura' }, ...(recDetail !== 'new' ? [{ id: 'recept', label: 'Recept' }] : []), ...(recEditData?.typ === 'PRO' ? [{ id: 'baleni', label: 'Balení' }, { id: 'ceny', label: 'Ceny' }, { id: 'eshop', label: 'E-shop' }, { id: 'pridavky', label: 'Ingredience' }, { id: 'combo', label: 'Combo' }] : []), ...(recDetail !== 'new' ? [{ id: 'sklad', label: 'Sklad' }] : [])].map(st => (
                                        <button key={st.id} onClick={() => { if (st.id === recDetailTab || (recDetailTab === 'receptura' && !guardReceptura())) return; setRecDetailTab(st.id); }}
                                            className="px-3 py-2 text-xs font-medium whitespace-nowrap"
                                            style={{ color: recDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                                borderBottom: `2px solid ${recDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1, flexShrink: 0 }}>
                                            {st.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Sub-tab content */}
                                <div className={recDetailTab === 'receptura' ? 'p-4' : 'p-4 overflow-y-auto'}
                                    style={{
                                        flex: 1, minHeight: 0,
                                        ...(recDetailTab === 'receptura' ? { overflow: 'hidden', display: 'flex', flexDirection: 'column' } : {}),
                                    }}>
                                    {/* ─ RECEPT TAB (read-only + PDF/mail) ─ */}
                                    {recDetailTab === 'recept' && recDetail !== 'new' && (
                                        <RecipeReadView receptId={recDetail} isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} isMobile={isMobile} />
                                    )}

                                    {/* ─ SKLAD TAB (placeholder) ─ */}
                                    {recDetailTab === 'baleni' && recDetailData && (
                                        <ReceptBaleni
                                            recept={recDetailData}
                                            isDark={isDark}
                                            onNavigate={onNavigate}
                                            onZmena={() => { loadRecDetail(recDetail, { keepTab: true }); refreshKalkulace(); loadRecepty(); }}
                                        />
                                    )}

                                    {/* Sklad: kuchyňská stanice a štítek na krabici (2026-10-23) — výdej
                                        a balení, ať se to nemíchá s ingrediencemi. */}
                                    {recDetailTab === 'sklad' && recDetail !== 'new' && recEditData?.typ === 'PRO' && (
                                        <div className="space-y-4 mb-4">
                                            <StaniceProduktu value={recEditData.kds_stanice_id} isDark={isDark}
                                                onChange={id => setRecEditData({ ...recEditData, kds_stanice_id: id })} />
                                            <StitekPolozky
                                                value={{ stitek: recEditData.stitek ?? null, stitek_skupina: recEditData.stitek_skupina ?? null }}
                                                zdedeno={(() => {
                                                    // Kategorie produktu a její předci — nejbližší s vlastní hodnotou.
                                                    const podleId = new Map(flatRecKatPRO.map(k => [k.id, k]));
                                                    const retez = [];
                                                    let k = podleId.get(Number(recEditData.kategorie_id));
                                                    for (let i = 0; k && i < 20; i++) { retez.push(k); k = k.parent_id ? podleId.get(k.parent_id) : null; }
                                                    return zdedenyStitek(retez);
                                                })()}
                                                navrhySkupin={flatRecKatPRO.map(k => k.stitek_skupina)}
                                                isDark={isDark}
                                                onChange={v => setRecEditData({ ...recEditData, ...v })} />
                                        </div>
                                    )}
                                    {recDetailTab === 'sklad' && recDetail !== 'new' && (
                                        <div className="rounded-xl p-10 text-center" style={{
                                            background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
                                            border: `1px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                                        }}>
                                            <Package size={30} style={{ color: mutedColor, opacity: 0.4, margin: '0 auto 10px' }} />
                                            <p className="text-sm font-medium" style={{ color: secondaryColor }}>Sklad</p>
                                            <p className="text-xs mt-1" style={{ color: mutedColor }}>Skladové zásoby a pohyby — připravujeme.</p>
                                        </div>
                                    )}

                                    {/* ── Modal: Jednotky (základní + sekundární) ── */}
                                    <JednotkyModal
                                        open={jednotkyModalOpen && !!recEditData} onClose={() => setJednotkyModalOpen(false)}
                                        isDark={isDark} jednotky={jednotky}
                                        jednotkaId={recEditData?.jednotka_id || ''}
                                        onJednotkaChange={v => setRecEditData({ ...recEditData, jednotka_id: v })}
                                        altJednotky={recAltJednotky}
                                        onAltJednotkyChange={alt => {
                                            setRecAltJednotky(alt);
                                            setRecDetailData(prev => prev ? { ...prev, alt_jednotky: alt } : prev);
                                            loadRecepty();
                                        }}
                                        api={api}
                                        listUrl={`/api/recepty/recepty/${recDetail}/alt-jednotky`}
                                        itemUrl={id => `/api/recepty/alt-jednotky-receptu/${id}`}
                                        isNew={recDetail === 'new'} novaHint="Nejdříve uložte položku." />
                                    {/* ─ CENY TAB ─ */}
                                    {recDetailTab === 'ceny' && recDetail !== 'new' && (
                                        <div>
                                            {recCeny.length > 0 && (
                                                <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    <table className="w-full">
                                                        <thead><tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                            <th style={thStyle}>Kanál</th>
                                                                            <th style={thStyle}>Cena bez DPH</th><th style={thStyle}>DPH</th>
                                                            <th style={thStyle}>S DPH</th><th style={thStyle}>Od</th><th style={thStyle}>Do</th><th style={thStyle}></th>
                                                        </tr></thead>
                                                        <tbody>{recCeny.map(c => (
                                                            <tr key={c.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` }}>
                                                                <td style={tdStyle}>
                                                                                {c.kanal ? (
                                                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                                                                        style={{ background: 'rgba(16,185,129,0.14)', color: '#10B981' }}>
                                                                                        {KANAL_NAZVY[c.kanal]}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-[11px]" style={{ color: mutedColor }}>všechny</span>
                                                                                )}
                                                                            </td>
                                                                            <td style={tdStyle}>{parseFloat(c.cena_bez_dph).toFixed(2)} Kč</td>
                                                                <td style={tdStyle}>{c.dph_sazba}%</td>
                                                                <td style={tdStyle}>{parseFloat(c.cena_s_dph).toFixed(2)} Kč</td>
                                                                <td style={tdStyle}>{fmtDate(c.platnost_od)}</td>
                                                                <td style={tdStyle}>{c.platnost_do ? fmtDate(c.platnost_do) : '—'}</td>
                                                                <td style={tdStyle}>
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => setRecCenaForm({ ...c, cena_s_dph: Math.round(parseFloat(c.cena_s_dph) * 100) / 100, platnost_od: c.platnost_od?.split('T')[0], platnost_do: c.platnost_do?.split('T')[0] || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}><Edit3 size={13} /></button>
                                                                        <button onClick={() => deleteRecCena(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}><Trash2 size={13} /></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}</tbody>
                                                    </table>
                                                </div>
                                            )}
                                            {!recCenaForm && (
                                                <button onClick={() => setRecCenaForm({ cena_s_dph: '', dph_sazba: 21, platnost_od: new Date().toISOString().split('T')[0], platnost_do: '' })} style={btnPrimary}>
                                                    <Plus size={14} /> Přidat cenu
                                                </button>
                                            )}
                                            {recCenaForm && (
                                                <div className="rounded-xl p-4 space-y-3 mt-2" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    <div className="grid grid-cols-4 gap-3">
                                                        {renderFormField('Kanál', <select value={recCenaForm.kanal || ''} onChange={e => setRecCenaForm({ ...recCenaForm, kanal: e.target.value || null })} style={selectStyle}>
                                                            <option value="" style={optionStyle}>Všechny kanály</option>
                                                            {Object.entries(KANAL_NAZVY).map(([k, v]) => <option key={k} value={k} style={optionStyle}>{v}</option>)}
                                                        </select>)}
                                                        {renderFormField('Cena s DPH *', <input type="number" step="0.01" value={recCenaForm.cena_s_dph} onChange={e => setRecCenaForm({ ...recCenaForm, cena_s_dph: e.target.value })} style={inputStyle} />)}
                                                        {renderFormField('DPH', <select value={recCenaForm.dph_sazba} onChange={e => setRecCenaForm({ ...recCenaForm, dph_sazba: parseInt(e.target.value) })} style={selectStyle}>
                                                            {dphSazby.map(d => <option key={d.sazba} value={d.sazba} style={optionStyle}>{d.nazev}</option>)}
                                                        </select>)}
                                                        {renderFormField('Poznámka', <input value={recCenaForm.poznamka || ''} onChange={e => setRecCenaForm({ ...recCenaForm, poznamka: e.target.value })} style={inputStyle} />)}
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {renderFormField('Platnost od *', <input type="date" value={recCenaForm.platnost_od || ''} onChange={e => setRecCenaForm({ ...recCenaForm, platnost_od: e.target.value })} style={inputStyle} />)}
                                                        {renderFormField('Platnost do', <input type="date" value={recCenaForm.platnost_do || ''} onChange={e => setRecCenaForm({ ...recCenaForm, platnost_do: e.target.value || null })} style={inputStyle} />)}
                                                    </div>
                                                    <p className="text-[11px]" style={{ color: mutedColor }}>
                                                        {recCenaForm.cena_s_dph !== '' && !isNaN(parseFloat(recCenaForm.cena_s_dph)) && (
                                                            <>Bez DPH {(parseFloat(recCenaForm.cena_s_dph) / (1 + (recCenaForm.dph_sazba || 0) / 100)).toFixed(2)} Kč. </>
                                                        )}
                                                        Cena bez kanálu platí všude. Cena pro konkrétní kanál ji přebíjí — hodí se,
                                                        když rozvoz stojí jinak než pult.
                                                    </p>
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => setRecCenaForm(null)} style={btnSecondary}>Zrušit</button>
                                                        <button onClick={saveRecCena} style={btnPrimary} disabled={recCenaSaving}>
                                                            {recCenaSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <ReceptPoplatekBaleni receptId={recDetail} isDark={isDark} onNavigate={onNavigate} />
                                        </div>
                                    )}
                                    {recDetailTab === 'ceny' && recDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte recept.</p>
                                    )}

                                    {/* ─ E-SHOP TAB ─ */}
                                    {recDetailTab === 'eshop' && recEditData?.typ === 'PRO' && recDetail !== 'new' && (
                                        <div className="grid gap-6 items-start" style={{ gridTemplateColumns: isWide ? 'minmax(0, 1fr) minmax(320px, 400px)' : 'minmax(0, 1fr)' }}>
                                        <div className="space-y-5">
                                            {/* Viditelnost */}
                                            <label className="flex items-center gap-3 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                                                <input type="checkbox" checked={recEditData.eshop_viditelny || false}
                                                    onChange={e => setRecEditData({ ...recEditData, eshop_viditelny: e.target.checked })} />
                                                <span>Zobrazit v e-shopu</span>
                                                {recEditData.eshop_viditelny && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>Viditelné</span>}
                                            </label>

                                            {/* Obrázek */}
                                            {renderFormField('Obrázek produktu', (
                                                <div className="flex items-start gap-3">
                                                    {eshopSelectedMedia ? (
                                                        <div className="relative" style={{ width: 120, height: 120, borderRadius: 7, overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                                                            <img src={`/storage/${eshopSelectedMedia.thumbnail_cesta || eshopSelectedMedia.cesta}`}
                                                                alt={eshopSelectedMedia.nazev}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        </div>
                                                    ) : (
                                                        <div style={{ width: 120, height: 120, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', border: `1px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                                                            <span style={{ fontSize: 11, color: mutedColor, textAlign: 'center' }}>Žádný<br/>obrázek</span>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col gap-2" style={{ minWidth: 210 }}>
                                                        {/* Nahrání z počítače je hlavní cesta, knihovna zůstává pod ním */}
                                                        <NahratMedia isDark={isDark} accept="image/*" vyska={84}
                                                            accent={SECTION_COLOR}
                                                            popisek="Přetáhni fotku nebo vyber z počítače"
                                                            onNahrano={(soubor) => {
                                                                setRecEditData(prev => ({ ...prev, eshop_media_soubor_id: soubor.id }));
                                                                setEshopSelectedMedia(soubor);
                                                                ulozEshopFotku(soubor);
                                                            }} />
                                                        <button onClick={() => {
                                                            setEshopMediaSearch('');
                                                            api('/api/media?typ=foto&per_page=48').then(r => setEshopMediaList(Array.isArray(r) ? r : (r.data || []))).catch(() => {});
                                                            setEshopMediaModal(true);
                                                        }} style={btnSecondary}>
                                                            <Image size={14} /> Vybrat z knihovny
                                                        </button>
                                                        {recEditData.eshop_media_soubor_id && (
                                                            <button onClick={() => { setRecEditData({ ...recEditData, eshop_media_soubor_id: null }); setEshopSelectedMedia(null); }}
                                                                style={{ ...btnSecondary, color: '#EF4444' }}>
                                                                <Trash2 size={14} /> Odstranit
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Popis */}
                                            {renderFormField('Popis pro e-shop', (
                                                <textarea value={recEditData.eshop_popis || ''} rows={6}
                                                    onChange={e => setRecEditData({ ...recEditData, eshop_popis: e.target.value })}
                                                    style={inputStyle} placeholder="Popis produktu zobrazený v e-shopu..." />
                                            ))}

                                            {/* Primární varianta */}
                                            {eshopVarianty.length > 1 && (
                                                <div className="rounded-xl p-4" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                                    <label className="flex items-center gap-2 text-sm cursor-pointer mb-2" style={{ color: 'var(--text-primary)' }}>
                                                        <input type="checkbox" checked={recEditData.eshop_primarni || false}
                                                            onChange={e => setRecEditData({ ...recEditData, eshop_primarni: e.target.checked })} />
                                                        <Star size={14} style={{ color: SECTION_COLOR }} />
                                                        <span>Primární varianta pro e-shop</span>
                                                    </label>
                                                    {/* Pokladna prodává jinak než e-shop: dlaždice v POSu přidá
                                                        na účet jedním ťuknutím právě tuhle variantu, takže tady
                                                        patří nejprodávanější velikost u pultu — ne ta, kterou
                                                        chceš mít nahoře v nabídce e-shopu. Proto vlastní přepínač
                                                        a ne sdílený s `eshop_primarni`. */}
                                                    <label className="flex items-center gap-2 text-sm cursor-pointer mb-2" style={{ color: 'var(--text-primary)' }}>
                                                        <input type="checkbox" checked={recEditData.pos_vychozi || false}
                                                            onChange={e => setRecEditData({ ...recEditData, pos_vychozi: e.target.checked })} />
                                                        <ShoppingCart size={14} style={{ color: SECTION_COLOR }} />
                                                        <span>Výchozí varianta v pokladně</span>
                                                    </label>
                                                    <p className="text-xs mt-1" style={{ color: mutedColor }}>
                                                        Varianty: {eshopVarianty.map(v => v.varianta || v.kod).join(', ')}
                                                        {(() => { const prim = eshopVarianty.find(v => v.eshop_primarni && v.id !== recDetail); return prim ? ` · Primární pro e-shop: ${prim.varianta || prim.kod}` : ''; })()}
                                                        {(() => { const vych = eshopVarianty.find(v => v.pos_vychozi && v.id !== recDetail); return vych ? ` · Výchozí v pokladně: ${vych.varianta || vych.kod}` : ''; })()}
                                                    </p>
                                                    <p className="text-xs mt-1" style={{ color: mutedColor }}>
                                                        Bez označení bere pokladna první variantu v pořadí kategorie.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        {/* Živý náhled — mění se hned při vyplňování (2026-10-26) */}
                                        <div style={isWide ? { position: 'sticky', top: 12 } : undefined}>
                                            <EshopNahled receptId={recDetail} nazev={recEditData.nazev} popis={recEditData.eshop_popis}
                                                fotoUrl={eshopSelectedMedia ? `/storage/${eshopSelectedMedia.cesta}` : (eshopVarianty.find(v => v.id === recDetail)?.foto_url || null)}
                                                mutedColor={mutedColor} />
                                        </div>
                                        </div>
                                    )}
                                    {recDetailTab === 'eshop' && recDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte produkt.</p>
                                    )}

                                    {/* ─ PŘÍDAVKY TAB (na PRO detailu) ─ */}
                                    {recDetailTab === 'pridavky' && recEditData?.typ === 'PRO' && recDetail !== 'new' && (
                                        <div className="space-y-4">
                                            <UpravyPoTuknuti value={recEditData.pos_upravy ?? null} isDark={isDark}
                                                zdedeno={(() => {
                                                    // Nejbližší kategorie s vlastní hodnotou — stejně jako katalog pokladny.
                                                    const podleId = new Map(flatRecKatPRO.map(k => [k.id, k]));
                                                    let k = podleId.get(Number(recEditData.kategorie_id));
                                                    for (let i = 0; k && i < 20; i++) {
                                                        if (k.pos_upravy != null) return !!k.pos_upravy;
                                                        k = k.parent_id ? podleId.get(k.parent_id) : null;
                                                    }
                                                    return false;
                                                })()}
                                                onChange={v => setRecEditData({ ...recEditData, pos_upravy: v })} />
                                            <PridavkyVCene value={recEditData.pridavky_v_cene ?? null} isDark={isDark}
                                                zdedeno={(() => {
                                                    const podleId = new Map(flatRecKatPRO.map(k => [k.id, k]));
                                                    let k = podleId.get(Number(recEditData.kategorie_id));
                                                    for (let i = 0; k && i < 20; i++) {
                                                        if (k.pridavky_v_cene != null) return k.pridavky_v_cene;
                                                        k = k.parent_id ? podleId.get(k.parent_id) : null;
                                                    }
                                                    return 0;
                                                })()}
                                                onChange={v => setRecEditData({ ...recEditData, pridavky_v_cene: v })} />
                                            <SlozeniProduktu produktId={recDetail} isDark={isDark}
                                                onZmena={() => setPridavkyKlic(k => k + 1)} />
                                            <NastaveniPridavku typ="produkt" id={recDetail} isDark={isDark} obnovit={pridavkyKlic}
                                                onOtevritUroven={(u) => {
                                                    if (u.typ !== 'kategorie' || !guardReceptura()) return;
                                                    setRecDetail(null); setRecDetailData(null); setRecEditData(null);
                                                    setKatDetail(u.id);
                                                }} />
                                        </div>
                                    )}
                                    {recDetailTab === 'pridavky' && recDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte produkt.</p>
                                    )}

                                    {/* ─ COMBO TAB (na PRO detailu) — dědí se z kategorie jako ingredience ─ */}
                                    {recDetailTab === 'combo' && recEditData?.typ === 'PRO' && recDetail !== 'new' && (
                                        <div className="space-y-3">
                                            <NastaveniPridavku druh="combo" typ="produkt" id={recDetail} isDark={isDark} obnovit={comboKlic}
                                                onOtevritUroven={(u) => {
                                                    if (u.typ !== 'kategorie' || !guardReceptura()) return;
                                                    setRecDetail(null); setRecDetailData(null); setRecEditData(null);
                                                    setKatDetail(u.id);
                                                }} />
                                        </div>
                                    )}
                                    {recDetailTab === 'combo' && recDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte produkt.</p>
                                    )}


                                    {/* ─ RECEPTURA TAB ─ */}
                                    {recDetailTab === 'receptura' && recDetail !== 'new' && (
                                        <RecepturaEditor
                                            key={`${recDetail}-${recDetailData?.jednotka_id || ''}-${recDetailData?.alt_jednotky?.length || 0}`}
                                            recept={recDetailData}
                                            isDark={isDark}
                                            stroje={stroje}
                                            jednotky={jednotky}
                                            kalkulace={kalkulace}
                                            onReceptUpdate={(updated, opts) => {
                                                const prevServer = recDetailData; // předchozí serverová hodnota (render-time)
                                                setRecDetailData(updated);
                                                // dávka/jednotka se drží i v recEditData (pill / JednotkyModal). Sesynchronizuj
                                                // je JEN když se uživatel neodchýlil od poslední serverové hodnoty — jinak by
                                                // tichý reload editoru (mount / přidání ingredience / reorder) přepsal
                                                // rozeditovanou jednotku starou hodnotou a Uložit by hlásil „chybí jednotka".
                                                setRecEditData(prev => {
                                                    if (!prev) return prev;
                                                    const next = { ...prev };
                                                    if ((prev.jednotka_id ?? '') === (prevServer?.jednotka_id ?? '')) next.jednotka_id = updated.jednotka_id;
                                                    if ((prev.pocet_porci ?? '') === (prevServer?.pocet_porci ?? '')) next.pocet_porci = updated.pocet_porci;
                                                    return next;
                                                });
                                                // Souhrn (počet ingrediencí napříč kroky) drží ProduktKvalita — po editaci
                                                // v editoru (přidání/smazání ingredience) ho přenačti z čerstvého kusovníku.
                                                setBomData(parseBomItems(updated.kusovnik || []));
                                                if (!opts?.silent) loadRecepty(); // seznam přenačti jen po reálné úpravě, ne při výběru
                                            }}
                                            onCostChanged={refreshKalkulace}
                                            onKalkulacePreview={setKalkulace}
                                            onNavigate={onNavigate}
                                            onDirtyChange={setReceptEditorDirty}
                                            saveRef={receptEditorSaveRef}
                                        />
                                    )}
                                    {recDetailTab === 'receptura' && recDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte recept.</p>
                                    )}

                                    {/* ── Modal: Revize (historie + přidání) ── */}
                                    {revHistModalOpen && recDetailData && (
                                        <div onClick={() => setRevHistModalOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                                        <div onClick={e => e.stopPropagation()} style={{ ...glassCard, width: 520, maxWidth: '95vw', maxHeight: '80vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Revize</h3>
                                            <button onClick={() => setRevHistModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                                        </div>
                                        <div className="p-4 overflow-y-auto">
                                            {/* Formulář nové revize */}
                                            <div className="rounded-xl p-4 mb-4" style={{
                                                background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                            }}>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <ClipboardList size={14} style={{ color: SECTION_COLOR }} />
                                                    <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Nová revize</span>
                                                </div>

                                                {/* Poznámka — návrh dle změn se předvyplní automaticky */}
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                    <Sparkles size={11} style={{ color: '#A855F7' }} />
                                                    <span className="text-[10px]" style={{ color: mutedColor }}>Návrh dle změn — uprav podle potřeby</span>
                                                </div>
                                                <textarea
                                                    value={revizePoznamka}
                                                    onChange={e => setRevizePoznamka(e.target.value)}
                                                    placeholder="Popište provedené změny…"
                                                    rows={3}
                                                    className="w-full rounded-lg px-3 py-2 text-xs mb-3"
                                                    style={{
                                                        background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
                                                        border: `1px solid ${!revizePoznamka.trim() ? 'rgba(239,68,68,0.3)' : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`,
                                                        color: 'var(--text-primary)', resize: 'vertical',
                                                    }}
                                                />

                                                <label className="flex items-start gap-2 mb-3" style={{ cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={revizeSeznameni} onChange={e => setRevizeSeznameni(e.target.checked)}
                                                        style={{ accentColor: SECTION_COLOR, width: 15, height: 15, marginTop: 1, cursor: 'pointer' }} />
                                                    <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                                                        Vyžaduje seznámení obsluhy
                                                        <span className="block text-[10px]" style={{ color: mutedColor }}>Zaměstnanci s recepty v Cockpitu změnu potvrdí. U drobností nezaškrtávej.</span>
                                                    </span>
                                                </label>

                                                <button
                                                    disabled={revizeSaving || !revizePoznamka.trim()}
                                                    onClick={async () => {
                                                        setRevizeSaving(true);
                                                        try {
                                                            const poznamka = revizePoznamka.trim();
                                                            const r = await api(`/api/recepty/recepty/${recDetail}/revize`, 'POST', { poznamka, vyzaduje_seznameni: revizeSeznameni });
                                                            setRevize(prev => [r, ...prev]);
                                                            setRevizePoznamka('');
                                                            setRevizeSeznameni(false);
                                                            loadRecepty();
                                                            setRecDetailData(prev => ({ ...prev, posledni_revize_at: r.created_at, posledni_revize_uzivatel: r.uzivatel }));
                                                        } catch (e) { setRecError(e.error || 'Chyba'); }
                                                        setRevizeSaving(false);
                                                    }}
                                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium"
                                                    style={{
                                                        background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer',
                                                        opacity: (revizeSaving || !revizePoznamka.trim()) ? 0.5 : 1,
                                                    }}
                                                >
                                                    {revizeSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                                    Zrevidováno
                                                </button>
                                            </div>

                                            {/* Historie revizí */}
                                            <div className="flex items-center gap-2 mb-3">
                                                <Clock size={14} style={{ color: SECTION_COLOR }} />
                                                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Historie revizí</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)', color: mutedColor }}>
                                                    {revize.length} {revize.length === 1 ? 'záznam' : revize.length < 5 ? 'záznamy' : 'záznamů'}
                                                </span>
                                            </div>

                                            {revize.length > 0 ? (
                                                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                                    {revize.map((rv, idx) => (
                                                        <div key={rv.id || idx} className="flex items-start gap-3 px-4 py-3" style={{
                                                            borderBottom: idx < revize.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` : 'none',
                                                            background: idx === 0 ? (isDark ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.03)') : 'transparent',
                                                        }}>
                                                            <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{
                                                                background: rv.poznamka === 'Bez úprav' ? (isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)') : (SECTION_COLOR + '20'),
                                                            }}>
                                                                {rv.poznamka === 'Bez úprav'
                                                                    ? <Check size={10} style={{ color: mutedColor }} />
                                                                    : <Edit3 size={10} style={{ color: SECTION_COLOR }} />}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>
                                                                        {fmtDate(rv.created_at)}
                                                                        {rv.created_at && ` ${new Date(rv.created_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`}
                                                                    </span>
                                                                    {rv.uzivatel && (
                                                                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)', color: secondaryColor }}>
                                                                            {rv.uzivatel.jmeno} {rv.uzivatel.prijmeni}
                                                                        </span>
                                                                    )}
                                                                    {rv.poznamka === 'Bez úprav' && (
                                                                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', color: mutedColor }}>
                                                                            Bez úprav
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {rv.poznamka && rv.poznamka !== 'Bez úprav' && (
                                                                    <p className="text-xs mt-1" style={{ color: secondaryColor }}>{rv.poznamka}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="rounded-xl p-6 text-center" style={{
                                                    background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                                }}>
                                                    <ClipboardList size={24} style={{ color: mutedColor, opacity: 0.3, margin: '0 auto' }} />
                                                    <p className="text-xs mt-2" style={{ color: mutedColor }}>Zatím žádná revize</p>
                                                </div>
                                            )}
                                        </div>
                                        </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                    )}
                    /* Třetí sloupec je v tomhle modulu standardně otevřený —
                       kalkulace a revize se u receptury čtou pořád. */
                    panel={() => katDetailNode ? (
                        <KategorieDopad key={katDetailNode.id} node={katDetailNode} pocty={katDetailPocty}
                            isDark={isDark} cardStyle={cardStyle} mutedColor={mutedColor} secondaryColor={secondaryColor}
                            jenIngredience={recTypFilter === 'PRO'}
                            obnovit={katPridavkyKlic} onVraceno={() => setKatPridavkyKlic(k => k + 1)}
                            onOtevrit={(m) => {
                                if (m.typ === 'kategorie') { setKatDetail(m.id); return; }
                                setKatDetail(null); loadRecDetail(m.id); setRecDetailTab('pridavky');
                            }} />
                    ) : recDetail !== 'new' && recDetailData ? (
                        <>
                            <RecipeCalcCard
                                recept={recDetailData} kalkulace={kalkulace} isPro={recDetailData.typ === 'PRO'}
                                onPouzito={() => setPouzitiModal({ typ: 'SEM', id: recDetailData.id })}
                                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                cardStyle={cardStyle}
                                bomLength={bomData.length} krokyLength={recDetailData.sop?.kroky?.length || 0}
                                pouzitoCount={recDetailData.kusovniky_jako_ingredience_count || 0}
                                calcLoading={calcLoading} />

                            {/* Poslední revize — vždy (fallback = datum vytvoření) */}
                            {(() => {
                                const rv = revize?.[0];
                                const jeRevize = !!(rv?.created_at || recDetailData.posledni_revize_at);
                                const kdy = rv?.created_at || recDetailData.posledni_revize_at || recDetailData.created_at;
                                const kdo = rv?.uzivatel || recDetailData.posledni_revize_uzivatel;
                                const kdoStr = kdo ? `${kdo.jmeno || ''} ${kdo.prijmeni || ''}`.trim() : null;
                                return (
                                    <div className="rounded-xl p-3 flex-shrink-0" style={{ ...cardStyle }}>
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                            <ClipboardList size={13} style={{ color: SECTION_COLOR }} />
                                            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: secondaryColor }}>{jeRevize ? 'Poslední revize' : 'Založeno'}</span>
                                            <button onClick={() => setRevHistModalOpen(true)} className="ml-auto text-[10px] font-medium flex items-center gap-0.5" style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>
                                                Historie <ChevronRight size={11} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] min-w-0 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                                            <span className="font-medium flex-shrink-0">{kdy ? fmtDate(kdy) : '—'}</span>
                                            {kdoStr && <span className="truncate" style={{ color: mutedColor }}>· {kdoStr}</span>}
                                            {!jeRevize && <span className="truncate" style={{ color: mutedColor }}>· zatím bez revize</span>}
                                        </div>
                                        {rv?.poznamka && (
                                            <p className="text-[11px] mt-1 line-clamp-2" style={{ color: secondaryColor }}>{rv.poznamka}</p>
                                        )}
                                    </div>
                                );
                            })()}

                        </>
                    ) : null}
                />
            )}

            {/* ── Kopírovat recepturu modal ── */}
            {copyBomModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                    onClick={e => { if (e.target === e.currentTarget) setCopyBomModal(false); }}>
                    <div style={{ ...glassCard, width: 500, maxWidth: '95vw', maxHeight: '80vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Kopírovat recepturu z jiné položky</h3>
                            <button onClick={() => setCopyBomModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                            <div style={{ position: 'relative' }}>
                                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: mutedColor }} />
                                <input placeholder="Hledat položku..." value={copyBomSearch} onChange={e => setCopyBomSearch(e.target.value)}
                                    autoFocus style={{ ...inputStyle, paddingLeft: 30, fontSize: 12 }} />
                            </div>
                        </div>
                        <div className="overflow-y-auto flex-1 p-1" style={{ maxHeight: 400 }}>
                            {recepty
                                .filter(r => r.id !== recDetail && r.kusovnik_count > 0)
                                .filter(r => {
                                    if (!copyBomSearch) return true;
                                    const q = copyBomSearch.toLowerCase();
                                    return r.nazev?.toLowerCase().includes(q) || r.kod?.toLowerCase().includes(q) || r.varianta?.toLowerCase().includes(q);
                                })
                                .slice(0, 50)
                                .map(r => (
                                    <div key={r.id} onClick={() => setCopyBomSourceId(r.id)}
                                        className="px-3 py-2.5 rounded-lg cursor-pointer mb-0.5"
                                        style={{
                                            background: copyBomSourceId === r.id ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)') : 'transparent',
                                            border: copyBomSourceId === r.id ? `1px solid ${SECTION_COLOR}40` : '1px solid transparent',
                                        }}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] font-bold" style={{ color: SECTION_COLOR }}>{r.kod}</span>
                                            <TypBadge typ={r.typ} />
                                            <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{r.nazev}</span>
                                            {r.varianta && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: secondaryColor }}>{r.varianta}</span>}
                                        </div>
                                    </div>
                                ))}
                            {recepty.filter(r => r.id !== recDetail).filter(r => {
                                if (!copyBomSearch) return true;
                                const q = copyBomSearch.toLowerCase();
                                return r.nazev?.toLowerCase().includes(q) || r.kod?.toLowerCase().includes(q);
                            }).length === 0 && (
                                <p className="text-center text-xs py-6" style={{ color: mutedColor }}>Žádné položky</p>
                            )}
                        </div>
                        <div className="flex items-center justify-between p-4" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <p className="text-[11px]" style={{ color: mutedColor }}>
                                {bomData.length > 0 ? 'Stávající receptura bude nahrazena.' : 'Receptura bude zkopírována do aktuální položky.'}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setCopyBomModal(false)} style={btnSecondary}>Zrušit</button>
                                <button onClick={() => copyBomFrom(copyBomSourceId)} style={btnPrimary}
                                    disabled={!copyBomSourceId || copyBomLoading}>
                                    {copyBomLoading ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Kopírovat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ═══════ TAB: INGREDIENCE (ADD) ═══════
                 Standardní rozvržení modulu (components/layout/ModulLayout, norma
                 standard-rozvrzeni.md) stejně jako produkty a suroviny: bez výběru
                 je seznam přehled přes celou šířku (mřížka po skupinách), po výběru
                 se zúží, vedle je detail a stálý panel (kalkulace + revize).
                 Mobil jede třístupňově jako Suroviny (`mobilRezim`); třetí sloupec
                 se tam nekreslí — náklad a marži nese záložka Karta. */}
            {tab === 'pridavky' && (
                <ModulLayout
                    isMobile={isMobile}
                    vybrano={!!addDetail}
                    mobilRezim
                    mobilPanel={false}
                    mobilRezervaDole={rezervaDole}
                    barva={SECTION_COLOR} isDark={isDark}
                    seznam={({ siroky }) => (
                        <IngredienceListPanel siroky={siroky} kompaktni={isMobile} rezervaDole={isMobile ? rezervaDole : null}
                            isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                            cardStyle={cardStyle} inputStyle={inputStyle}
                            search={addSearch} setSearch={setAddSearch}
                            skupiny={addSkupiny} items={addRecepty} loading={addLoading}
                            activeId={addDetail}
                            onPoradi={nastavAddPoradi}
                            onSelect={async (id) => { if (await guardAddReceptura()) loadAddDetail(id); }}
                            onNew={novaIngredience}
                            onSkupinaKlik={async () => {
                                if (!addDetail) return 'nic';                         // už jsme v přehledu → obyčejný toggle
                                if (!(await guardAddReceptura())) return 'blokovano'; // neuložené změny drží detail otevřený
                                zavritAddDetail();
                                return 'prehled';
                            }}
                            onEditSkupina={(s) => {
                                setAddSkupinyForm({ id: s.id, nazev: s.nazev, typ_vyberu: s.typ_vyberu, min_vyberu: s.min_vyberu, max_vyberu: s.max_vyberu, povinny: s.povinny, poradi: s.poradi, aktivni: s.aktivni, vychozi_min_ks: s.vychozi_min_ks ?? 0, vychozi_max_ks: s.vychozi_max_ks ?? 1 });
                                setAddSkupinyModalOpen(true);
                            }}
                            onManageSkupiny={() => { setAddSkupinyForm(null); setAddSkupinyModalOpen(true); }}
                            onMoveToSkupina={(ids, cilId, zdrojId) => {
                                // Přídavek může být ve víc skupinách — přetažení proto jen odebere
                                // zdrojovou a přidá cílovou, ostatní zařazení nechá být.
                                const zmeny = [];
                                for (const id of ids) {
                                    const it = addRecepty.find(r => r.id === id);
                                    if (!it) continue;
                                    const puvodni = (it.pridavky_skupiny || []).map(s => s.id);
                                    const nove = puvodni.filter(x => x !== zdrojId);
                                    if (cilId && !nove.includes(cilId)) nove.push(cilId);
                                    if (nove.length === puvodni.length && nove.every(x => puvodni.includes(x))) continue;
                                    zmeny.push({ id, nove });
                                }
                                if (zmeny.length === 0) return;

                                // Optimisticky — řádek přeskočí do cílové sekce hned.
                                setAddRecepty(prev => prev.map(r => {
                                    const z = zmeny.find(x => x.id === r.id);
                                    if (!z) return r;
                                    return { ...r, pridavky_skupiny: z.nove.map(sid => addSkupiny.find(s => s.id === sid)).filter(Boolean) };
                                }));

                                Promise.all(zmeny.map(z => api(`/api/recepty/pridavky/${z.id}/skupiny`, 'PUT', { skupina_ids: z.nove })))
                                    .then(() => { if (zmeny.some(z => z.id === addDetail)) loadAddDetail(addDetail, { keepTab: true }); })
                                    .catch(e => { alert(e.error || 'Přesun se nezdařil'); loadAddRecepty({ silent: true }); });
                            }}
                        />
                    )}
                    detail={() => {
                        const hranice = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`;
                        const anyDirty = addDirty || addEditorDirty;
                        const busy = addSaving || addTopSaving;
                        const ulozitVypnuto = busy || (!anyDirty && addDetail !== 'new');
                        const vybraneSkupiny = addSkupiny.filter(s => (addEditData?.skupina_ids || []).includes(s.id));
                        const jednotkaZkr = addEditData?.jednotka_id ? (jednotky.find(j => j.id === addEditData.jednotka_id)?.zkratka || 'jedn.') : null;
                        const aktivni = addEditData?.aktivni ?? true;

                        // Uložit: nejdřív koncept editoru receptury, pak revize s návrhem textu.
                        const ulozit = async () => {
                            if (!addEditData?.nazev?.trim()) { alert('Zadejte název.'); return; }
                            if (addDetail === 'new') { saveAddRecept(); return; }
                            // Zachyť dirty editoru PŘED uložením — saveAll ho přes onDirtyChange(false) vynuluje.
                            const editorWasDirty = addEditorDirty;
                            if (addEditorDirty && addEditorSaveRef.current) {
                                try { setAddTopSaving(true); await addEditorSaveRef.current(); } finally { setAddTopSaving(false); }
                            }
                            if (addDirty || editorWasDirty) { setAddRevizePoznamka(''); setAddSaveRevModalOpen(true); }
                        };

                        const ulozitTlacitko = (
                            <button onClick={ulozit} disabled={ulozitVypnuto}
                                style={{ ...btnPrimary, padding: isMobile ? '0 16px' : '6px 14px', ...(isMobile ? { minHeight: 40 } : null), flexShrink: 0, position: 'relative', opacity: ulozitVypnuto ? 0.55 : 1 }}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                {anyDirty && addDetail !== 'new' && (
                                    <span style={{ position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: `2px solid ${isDark ? '#1a1a2e' : '#fff'}` }} />
                                )}
                            </button>
                        );

                        // Příplatek, náklad a marže z kalkulace — karta (mobil) i hero je čtou odsud.
                        const kanaly = Object.values(addKalkulace?.kanaly || {});
                        const priplatky = kanaly.map(k => Number(k.cena_s_dph) || 0).filter(c => c > 0);
                        const marze = kanaly.map(k => k.marze_pct).filter(v => v != null);
                        const nejhorsiMarzeAdd = marze.length ? Math.min(...marze) : null;

                        // Výběr skupin — v hlavičce (desktop) jako rozbalovací panel, na kartě (mobil) řádky po 44 px.
                        const prepniSkupinu = (s) => {
                            const ids = addEditData.skupina_ids || [];
                            const on = ids.includes(s.id);
                            setAddEditData({ ...addEditData, skupina_ids: on ? ids.filter(x => x !== s.id) : [...ids, s.id] });
                        };

                        return (
                            /* Na mobilu je detail rovnou plocha displeje, ne karta (§1b). */
                            <div className={`overflow-hidden flex flex-col flex-1 ${isMobile ? '' : 'rounded-xl'}`}
                                style={isMobile ? { minHeight: 0 } : { ...cardStyle, minHeight: 0 }}>
                                {isMobile ? (
                                    /* Mobil: hlavička je navigace, ne formulář (§1e) —
                                       Zpět · název · Uložit, pod tím kód · skupiny · jednotka jako text. */
                                    <div className="flex flex-col" style={{ padding: '8px 12px 10px', gap: 6, flexShrink: 0, borderBottom: hranice }}>
                                        <div className="flex items-center gap-2">
                                            <ZpetTlacitko onClick={async () => { if (await guardAddReceptura()) zavritAddDetail(); }} popis="Zpět na seznam" />
                                            <span className="min-w-0 flex-1 truncate text-[15px] font-bold" style={{ color: 'var(--text-primary)' }}>
                                                {addEditData?.nazev?.trim() || (addDetail === 'new' ? 'Nová ingredience' : '…')}
                                            </span>
                                            {ulozitTlacitko}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px] min-w-0" style={{ color: mutedColor }}>
                                            {vybraneSkupiny.length > 0 && <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: getKatVisual(vybraneSkupiny[0].nazev).color }} />}
                                            <span className="truncate">
                                                {[addDetail !== 'new' ? addDetailData?.kod : null, vybraneSkupiny.map(s => s.nazev).join(', ') || 'Bez skupiny', jednotkaZkr].filter(Boolean).join(' · ')}
                                            </span>
                                            {!aktivni && (
                                                <span className="px-1.5 py-0.5 rounded flex-shrink-0 font-semibold" style={{ background: 'rgba(148,163,184,0.18)', color: secondaryColor }}>Archiv</span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                <div className="flex items-start justify-between p-4" style={{ borderBottom: hranice }}>
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        {addDetail !== 'new' && addDetailData && (
                                            <div className="flex flex-col items-center gap-1" style={{ flexShrink: 0 }}>
                                                <button onClick={() => setAddIconPickerOpen(true)} title="Změnit ikonu / obrázek"
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', position: 'relative' }}
                                                    className="group">
                                                    <RecipeKatIcon foto={addDetailData.foto_url} ikona={addDetailData.ikona}
                                                        kategorieNazev={(addDetailData.pridavky_skupiny || [])[0]?.nazev || addDetailData.nazev} size={58} radius={9} />
                                                    <span className="hidden group-hover:flex" style={{ position: 'absolute', inset: 0, borderRadius: 9, background: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Edit3 size={18} style={{ color: '#fff' }} />
                                                    </span>
                                                </button>
                                                <span className="text-[10px] tabular-nums" style={{ color: mutedColor }} title="Evidenční kód položky">{addDetailData.kod}</span>
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            {addEditData ? (() => {
                                                const pill = { background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` };
                                                const skupinyBarva = vybraneSkupiny.length ? getKatVisual(vybraneSkupiny[0].nazev).color : null;
                                                return (
                                                    <>
                                                        {/* Řádek: archiv + název */}
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {!aktivni && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" title="Položka je v archivu — v nabídce se nezobrazuje"
                                                                    style={{ background: 'rgba(148,163,184,0.16)', color: '#94A3B8', flexShrink: 0 }}>
                                                                    <Archive size={10} /> Archiv
                                                                </span>
                                                            )}
                                                            <NazevInput value={addEditData.nazev || ''} onChange={e => setAddEditData({ ...addEditData, nazev: e.target.value })}
                                                                placeholder="Název ingredience"
                                                                style={{ fontSize: 16, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--text-primary)', outline: 'none', padding: '2px 4px', borderRadius: 5, ...dirtyRing(addFieldDirty('nazev')) }} />
                                                        </div>
                                                        {/* Řádek 2: skupiny + jednotka + alergeny + validace */}
                                                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                                            <div style={{ position: 'relative' }}>
                                                                <button onClick={() => setAddSkupinyPickerOpen(o => !o)} title="Zařazení do skupin ingrediencí"
                                                                    className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2 pr-2.5"
                                                                    style={{ ...pill, cursor: 'pointer', ...dirtyRing(addFieldDirty('skupina_ids')) }}>
                                                                    <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: skupinyBarva || 'transparent', border: skupinyBarva ? 'none' : `1px solid ${mutedColor}` }} />
                                                                    <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)', maxWidth: 200 }}>
                                                                        {vybraneSkupiny.length ? vybraneSkupiny.map(s => s.nazev).join(', ') : 'Bez skupiny'}
                                                                    </span>
                                                                    <ChevronDown size={12} style={{ color: mutedColor, flexShrink: 0 }} />
                                                                </button>
                                                                {addSkupinyPickerOpen && (<>
                                                                    <div onClick={() => setAddSkupinyPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                                                                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 41, width: 240, maxHeight: 280, overflowY: 'auto', borderRadius: 9, padding: 6, ...glassCard }}>
                                                                        {addSkupiny.filter(s => s.aktivni).length === 0 && (
                                                                            <p className="text-[11px] px-2 py-2" style={{ color: mutedColor }}>Žádné aktivní skupiny.</p>
                                                                        )}
                                                                        {addSkupiny.filter(s => s.aktivni).map(s => {
                                                                            const on = (addEditData.skupina_ids || []).includes(s.id);
                                                                            return (
                                                                                <button key={s.id} onClick={() => prepniSkupinu(s)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left"
                                                                                    style={{ background: on ? SECTION_BG : 'transparent', border: 'none', cursor: 'pointer' }}>
                                                                                    <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? SECTION_COLOR : 'transparent', border: `1px solid ${on ? SECTION_COLOR : mutedColor}` }}>
                                                                                        {on && <Check size={10} style={{ color: '#fff' }} />}
                                                                                    </span>
                                                                                    <span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev}</span>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                        <button onClick={() => { setAddSkupinyPickerOpen(false); setAddSkupinyForm(null); setAddSkupinyModalOpen(true); }}
                                                                            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] mt-1"
                                                                            style={{ color: mutedColor, background: 'none', border: 'none', cursor: 'pointer', borderTop: hranice }}>
                                                                            <Settings2 size={12} /> Spravovat skupiny
                                                                        </button>
                                                                    </div>
                                                                </>)}
                                                            </div>
                                                            {/* Jednotky stejným modalem jako u produktu (základní + sekundární) */}
                                                            <button onClick={() => setAddJednotkyOpen(true)} title="Nastavit jednotky"
                                                                className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2 pr-2.5" style={{ background: pill.background, border: `1px solid ${!addEditData.jednotka_id ? 'rgba(239,68,68,0.5)' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`, cursor: 'pointer', ...dirtyRing(addFieldDirty('jednotka_id')) }}>
                                                                <Scale size={12} style={{ color: !addEditData.jednotka_id ? '#EF4444' : mutedColor, flexShrink: 0 }} />
                                                                <span className="text-xs font-medium" style={{ color: addEditData.jednotka_id ? 'var(--text-primary)' : '#EF4444' }}>
                                                                    {jednotkaZkr || 'Jednotka *'}
                                                                    {addAltJednotky.length > 0 && <span style={{ color: mutedColor, fontWeight: 400 }}> +{addAltJednotky.length}</span>}
                                                                </span>
                                                            </button>
                                                            {isWide && addDetail !== 'new' && addDetailData && (
                                                                <RecipeAlergenyInline recept={addDetailData} bomLength={addDetailData.kusovnik?.length || 0} refreshTick={addRefreshTick} />
                                                            )}
                                                            {addDetail !== 'new' && <RecipeValidaceUpozorneni validace={addDetailData?.validace} mutedColor={mutedColor} />}
                                                        </div>
                                                    </>
                                                );
                                            })() : (
                                                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{addDetail === 'new' ? 'Nová ingredience' : '...'}</h3>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                                        {addEditData && (
                                            <AkceMenu
                                                isDark={isDark} mutedColor={mutedColor} glassCard={glassCard}
                                                polozky={[
                                                    {
                                                        id: 'archiv',
                                                        label: aktivni ? 'Archivovat' : 'Obnovit z archivu',
                                                        icon: aktivni ? Archive : ArchiveRestore,
                                                        onClick: () => setAddEditData({ ...addEditData, aktivni: !aktivni }),
                                                    },
                                                    {
                                                        // Mazání jen na desktopu — na mobilu by sedělo kousek od Uložit (§1b).
                                                        id: 'smazat',
                                                        label: 'Smazat',
                                                        icon: Trash2,
                                                        danger: true,
                                                        hidden: addDetail === 'new',
                                                        onClick: () => setSmazatModal({ typ: 'pridavek', id: addDetail, nazev: addDetailData?.nazev || addEditData.nazev }),
                                                    },
                                                ]}
                                            />
                                        )}
                                        {ulozitTlacitko}
                                    </div>
                                </div>
                                )}

                                {addError && <div className="text-xs" style={{ color: '#EF4444', padding: isMobile ? '8px 12px 0' : '8px 16px 0', flexShrink: 0 }}>{addError}</div>}

                                <JednotkyModal
                                    open={addJednotkyOpen && !!addEditData} onClose={() => setAddJednotkyOpen(false)}
                                    isDark={isDark} jednotky={jednotky}
                                    jednotkaId={addEditData?.jednotka_id || ''}
                                    onJednotkaChange={v => setAddEditData({ ...addEditData, jednotka_id: v })}
                                    altJednotky={addAltJednotky}
                                    onAltJednotkyChange={alt => {
                                        setAddAltJednotky(alt);
                                        setAddDetailData(prev => prev ? { ...prev, alt_jednotky: alt } : prev);
                                        loadAddRecepty({ silent: true });
                                    }}
                                    api={api}
                                    listUrl={`/api/recepty/recepty/${addDetail}/alt-jednotky`}
                                    itemUrl={id => `/api/recepty/alt-jednotky-receptu/${id}`}
                                    isNew={addDetail === 'new'} novaHint="Nejdříve uložte ingredienci." />

                                {/* Hero — metriky, když není stálý třetí sloupec. Na mobilu je nese záložka Karta. */}
                                {addDetail !== 'new' && addDetailData && !isWide && !isMobile && (
                                    <RecipeHero recept={addDetailData} kalkulace={addKalkulace} bomLength={addDetailData.kusovnik?.length || 0}
                                        onPouzito={() => setPouzitiModal({ typ: 'SEM', id: addDetailData.id })}
                                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} isMobile={isMobile} />
                                )}

                                {/* Sub-tabs — na mobilu v jedné přejeté řadě, cíle 44 px */}
                                <div className="flex gap-1 pt-3" style={{
                                    flexShrink: 0, paddingLeft: isMobile ? 12 : 16, paddingRight: isMobile ? 12 : 16,
                                    borderBottom: hranice, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
                                }}>
                                    {[
                                        // „Karta" jen na mobilu — na desktopu ta pole nese hlavička (§1e).
                                        ...(isMobile ? [{ id: 'karta', label: 'Karta', dirty: ['nazev', 'jednotka_id', 'aktivni', 'poznamka', 'skupina_ids'].some(addFieldDirty) }] : []),
                                        { id: 'receptura', label: 'Receptura', dirty: addEditorDirty },
                                        ...(addDetail !== 'new' ? [{ id: 'recept', label: 'Recept' }] : []),
                                        { id: 'ceny', label: 'Příplatek' },
                                        { id: 'info', label: 'Info' },
                                        ...(addDetail !== 'new' ? [{ id: 'revize', label: 'Revize' }] : []),
                                    ].map(st => (
                                        <button key={st.id} onClick={async () => {
                                            if (st.id === addDetailTab) return;
                                            if (addDetailTab === 'receptura' && !(await guardAddReceptura())) return;
                                            setAddDetailTab(st.id);
                                        }}
                                            className={`font-medium whitespace-nowrap ${isMobile ? 'px-4 text-[13px]' : 'px-3 py-2 text-xs'}`}
                                            style={{ color: addDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                                borderBottom: `2px solid ${addDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1, flexShrink: 0,
                                                display: 'flex', alignItems: 'center', gap: 5,
                                                ...(isMobile ? { minHeight: 44, WebkitTapHighlightColor: 'transparent' } : null) }}>
                                            {st.label}
                                            {st.dirty && <span style={{ width: 7, height: 7, borderRadius: '50%', background: SECTION_COLOR, flexShrink: 0 }} />}
                                        </button>
                                    ))}
                                </div>

                                {/* Sub-tab content — dole rezerva, ať konec obsahu jde vyscrollovat nad Palec */}
                                <div className={addDetailTab === 'receptura' ? '' : 'overflow-y-auto'}
                                    style={{
                                        flex: 1, minHeight: 0, overscrollBehavior: 'contain',
                                        padding: isMobile ? 12 : 16,
                                        ...(rezervaDole ? { paddingBottom: `calc(${rezervaDole} + 12px)` } : null),
                                        ...(addDetailTab === 'receptura' ? { overflow: 'hidden', display: 'flex', flexDirection: 'column' } : {}),
                                    }}>
                                    {/* ─ KARTA TAB (jen mobil) — řádky v hustotě iOS nastavení, 44 px ─ */}
                                    {isMobile && addDetailTab === 'karta' && addEditData && (() => {
                                        const jemna = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)';
                                        const radek = (label, obsah, jeDirty = false, onClick = null) => (
                                            <div onClick={onClick || undefined} className="flex items-center gap-3" style={{
                                                minHeight: 44, borderBottom: `1px solid ${jemna}`, cursor: onClick ? 'pointer' : undefined,
                                                ...(jeDirty ? { boxShadow: 'inset 3px 0 0 rgba(245,158,11,0.75)', paddingLeft: 9 } : null),
                                            }}>
                                                <span className="text-[13px] flex-shrink-0" style={{ color: secondaryColor, width: 96 }}>{label}</span>
                                                <div className="flex-1 min-w-0 flex items-center justify-end gap-2">{obsah}</div>
                                            </div>
                                        );
                                        const hodnota = { fontSize: 14, color: 'var(--text-primary)' };
                                        const text = (v, barva) => <span className="truncate" style={{ ...hodnota, color: barva || hodnota.color }}>{v}</span>;
                                        const sipkaVpravo = <ChevronRight size={15} style={{ color: mutedColor, flexShrink: 0 }} />;
                                        const marzeBarva = nejhorsiMarzeAdd == null ? null : nejhorsiMarzeAdd < PRAH_MARZE.kriticka ? MARZE_BARVY.kriticka : nejhorsiMarzeAdd >= PRAH_MARZE.dobra ? MARZE_BARVY.dobra : null;
                                        return (
                                            <div>
                                                {radek('Název', (
                                                    <input value={addEditData.nazev || ''} onChange={e => setAddEditData({ ...addEditData, nazev: e.target.value })}
                                                        placeholder="Název ingredience"
                                                        style={{ ...hodnota, width: '100%', textAlign: 'right', background: 'transparent', border: 'none', outline: 'none', padding: '8px 0', fontWeight: 600 }} />
                                                ), addFieldDirty('nazev'))}
                                                {radek('Kód', addDetail === 'new'
                                                    ? text('vygeneruje se (ADD-…)', mutedColor)
                                                    : text(addDetailData?.kod || '—'))}
                                                {radek('Skupiny', (<>
                                                    {text(vybraneSkupiny.map(s => s.nazev).join(', ') || 'Bez skupiny', vybraneSkupiny.length ? null : '#EF4444')}
                                                    <ChevronDown size={15} style={{ color: mutedColor, flexShrink: 0, transform: addSkupinyPickerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                                                </>), addFieldDirty('skupina_ids'), () => setAddSkupinyPickerOpen(o => !o))}
                                                {/* Výběr skupin rovnou pod řádkem — zaškrtávací řádky po 44 px */}
                                                {addSkupinyPickerOpen && (
                                                    <div style={{ borderBottom: `1px solid ${jemna}` }}>
                                                        {addSkupiny.filter(s => s.aktivni).length === 0 && (
                                                            <p className="text-[12px] py-3" style={{ color: mutedColor }}>Žádné aktivní skupiny.</p>
                                                        )}
                                                        {addSkupiny.filter(s => s.aktivni).map(s => {
                                                            const on = (addEditData.skupina_ids || []).includes(s.id);
                                                            return (
                                                                <button key={s.id} onClick={() => prepniSkupinu(s)} className="w-full flex items-center gap-3 text-left"
                                                                    style={{ minHeight: 44, paddingLeft: 12, background: 'none', border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                                                                    <span style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? SECTION_COLOR : 'transparent', border: `1.5px solid ${on ? SECTION_COLOR : mutedColor}` }}>
                                                                        {on && <Check size={13} style={{ color: '#fff' }} />}
                                                                    </span>
                                                                    <span className="text-[14px] truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {radek('Jednotka', (
                                                    <button onClick={() => setAddJednotkyOpen(true)} className="flex items-center gap-1.5"
                                                        style={{ ...hodnota, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', color: addEditData.jednotka_id ? 'var(--text-primary)' : '#EF4444' }}>
                                                        {jednotkaZkr || 'Vybrat *'}
                                                        {addAltJednotky.length > 0 && <span style={{ color: mutedColor }}>+{addAltJednotky.length}</span>}
                                                        {sipkaVpravo}
                                                    </button>
                                                ), addFieldDirty('jednotka_id'))}
                                                {radek('Aktivní', (
                                                    <button onClick={() => setAddEditData({ ...addEditData, aktivni: !aktivni })} aria-pressed={aktivni}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ width: 42, height: 26, borderRadius: 999, position: 'relative', transition: 'background 0.2s', background: aktivni ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)') }}>
                                                            <span style={{ position: 'absolute', top: 3, left: aktivni ? 19 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
                                                        </span>
                                                    </button>
                                                ), addFieldDirty('aktivni'))}
                                                {addDetail !== 'new' && (<>
                                                    {radek('Příplatek', (<>
                                                        {priplatky.length
                                                            ? text(`${Math.min(...priplatky) === Math.max(...priplatky) ? priplatky[0].toFixed(2) : `${Math.min(...priplatky).toFixed(2)}–${Math.max(...priplatky).toFixed(2)}`} Kč s DPH`)
                                                            : text('bez ceny', '#EF4444')}
                                                        {sipkaVpravo}
                                                    </>), false, () => setAddDetailTab('ceny'))}
                                                    {radek('Náklad', text(addKalkulace?.total_cost != null ? `${Number(addKalkulace.total_cost).toFixed(2)} Kč${jednotkaZkr ? ` / ${jednotkaZkr}` : ''}` : '—'))}
                                                    {radek('Marže', text(nejhorsiMarzeAdd != null ? `${Math.round(nejhorsiMarzeAdd)} %` : '—', marzeBarva))}
                                                    {radek('Alergeny', <RecipeAlergenyInline recept={addDetailData} bomLength={addDetailData?.kusovnik?.length || 0} refreshTick={addRefreshTick} />)}
                                                    {addDetailData?.validace?.stav && (
                                                        <div className="pt-3"><RecipeValidaceUpozorneni validace={addDetailData.validace} mutedColor={mutedColor} /></div>
                                                    )}
                                                </>)}
                                                {/* Poznámka je jediné víceřádkové pole, takže dostane vlastní blok */}
                                                <div className="pt-3">
                                                    <label className="block text-[13px] mb-1.5" style={{ color: secondaryColor }}>Poznámka</label>
                                                    <textarea value={addEditData.poznamka || ''} onChange={e => setAddEditData({ ...addEditData, poznamka: e.target.value })}
                                                        rows={3} placeholder="—" style={{ ...inputStyle, ...(addFieldDirty('poznamka') ? { borderColor: SECTION_COLOR } : null) }} />
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* ─ RECEPTURA TAB — stejný krokový editor jako u produktů ─ */}
                                    {addDetailTab === 'receptura' && addDetail !== 'new' && addDetailData && (
                                        <RecepturaEditor
                                            key={`add-${addDetail}-${addDetailData?.jednotka_id || ''}-${addDetailData?.alt_jednotky?.length || 0}`}
                                            recept={addDetailData}
                                            isDark={isDark}
                                            stroje={stroje}
                                            jednotky={jednotky}
                                            kalkulace={addKalkulace}
                                            onReceptUpdate={(updated, opts) => {
                                                const prevServer = addDetailData; // poslední serverová hodnota (render-time)
                                                // Detail přídavku nese navíc skupiny — obecný endpoint je nevrací,
                                                // takže je z předchozího stavu přeneseme, ať pill v hlavičce nezmizí.
                                                setAddDetailData(prev => ({ ...updated, pridavky_skupiny: updated.pridavky_skupiny ?? prev?.pridavky_skupiny ?? [] }));
                                                // Jednotku sesynchronizuj jen když ji uživatel v hlavičce nerozeditoval —
                                                // jinak by tichý reload editoru přepsal rozdělanou volbu starou hodnotou.
                                                setAddEditData(prev => {
                                                    if (!prev) return prev;
                                                    if ((prev.jednotka_id ?? '') !== (prevServer?.jednotka_id ?? '')) return prev;
                                                    return { ...prev, jednotka_id: updated.jednotka_id || '' };
                                                });
                                                if (!opts?.silent) loadAddRecepty({ silent: true });
                                            }}
                                            onCostChanged={refreshAddKalkulace}
                                            onKalkulacePreview={setAddKalkulace}
                                            onNavigate={onNavigate}
                                            onDirtyChange={setAddEditorDirty}
                                            saveRef={addEditorSaveRef}
                                        />
                                    )}
                                    {addDetailTab === 'receptura' && addDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte ingredienci.</p>
                                    )}

                                    {/* ─ RECEPT TAB (read-only + PDF/mail) ─ */}
                                    {addDetailTab === 'recept' && addDetail !== 'new' && (
                                        <RecipeReadView receptId={addDetail} isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} isMobile={isMobile} />
                                    )}

                                    {/* ─ PŘÍPLATEK (CENY) TAB ─ */}
                                    {addDetailTab === 'ceny' && addDetail !== 'new' && (
                                        <PriplatekCeny
                                            ceny={addCeny} form={addCenaForm} setForm={setAddCenaForm}
                                            saving={addCenaSaving} onSave={ulozAddCenu} onDelete={smazAddCenu}
                                            naklad={addKalkulace?.total_cost ?? null} dphSazby={dphSazby} kanalNazvy={KANAL_NAZVY}
                                            isMobile={isMobile} isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                            inputStyle={inputStyle} selectStyle={selectStyle} optionStyle={optionStyle}
                                            btnPrimary={btnPrimary} btnSecondary={btnSecondary} thStyle={thStyle} tdStyle={tdStyle}
                                            renderFormField={renderFormField} />
                                    )}
                                    {addDetailTab === 'ceny' && addDetail === 'new' && (
                                        <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte ingredienci.</p>
                                    )}

                                    {/* ─ INFO TAB ─ */}
                                    {addDetailTab === 'info' && addEditData && (
                                        <div style={{ maxWidth: 620 }}>
                                            {infoRow('Kód', addDetail === 'new'
                                                ? <span className="text-xs italic" style={{ color: mutedColor }}>Vygeneruje se automaticky (ADD-XXX)</span>
                                                : <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{addDetailData?.kod || '—'}</span>)}
                                            {infoRow('Skupiny', (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {vybraneSkupiny.map(s => (
                                                        <span key={s.id} className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: SECTION_BG, color: SECTION_COLOR }}>{s.nazev}</span>
                                                    ))}
                                                    {vybraneSkupiny.length === 0 && (
                                                        <span className="text-xs" style={{ color: mutedColor }}>Bez skupiny — ingredience se zatím nikde nenabídne.</span>
                                                    )}
                                                </div>
                                            ), { alignTop: true })}
                                            {infoRow('Poznámka', (
                                                <textarea value={addEditData.poznamka || ''} rows={3}
                                                    onChange={e => setAddEditData({ ...addEditData, poznamka: e.target.value })}
                                                    style={{ ...inputStyle, resize: 'vertical', ...dirtyRing(addFieldDirty('poznamka')) }} />
                                            ), { alignTop: true, last: true })}
                                        </div>
                                    )}

                                    {/* ─ REVIZE TAB ─ */}
                                    {addDetailTab === 'revize' && addDetail !== 'new' && (
                                        <div className="space-y-3">
                                            {addRevize.length > 0 ? addRevize.map(rev => (
                                                <div key={rev.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg" style={{
                                                    background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                                                }}>
                                                    <ClipboardList size={14} style={{ color: SECTION_COLOR, flexShrink: 0, marginTop: 2 }} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs" style={{ color: 'var(--text-primary)' }}>{rev.poznamka || '(bez poznámky)'}</p>
                                                        <p className="text-[10px] mt-1" style={{ color: mutedColor }}>
                                                            {rev.uzivatel ? `${rev.uzivatel.jmeno} ${rev.uzivatel.prijmeni}` : '—'} · {fmtDate(rev.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            )) : (
                                                <p className="text-xs py-4" style={{ color: mutedColor }}>Zatím žádné revize.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }}
                    /* Třetí sloupec — kalkulace a poslední revize se u receptury čtou pořád. */
                    panel={() => (addDetail !== 'new' && addDetailData ? (
                        <>
                            <RecipeCalcCard
                                recept={addDetailData} kalkulace={addKalkulace} isPro={false} prodava
                                onPouzito={() => setPouzitiModal({ typ: 'SEM', id: addDetailData.id })}
                                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                                cardStyle={cardStyle}
                                bomLength={addDetailData.kusovnik?.length || 0} krokyLength={addDetailData.sop?.kroky?.length || 0}
                                pouzitoCount={addDetailData.kusovniky_jako_ingredience_count || 0}
                                calcLoading={addCalcLoading} />

                            {/* Poslední revize — vždy (fallback = datum vytvoření) */}
                            {(() => {
                                const rv = addRevize?.[0];
                                const jeRevize = !!(rv?.created_at || addDetailData.posledni_revize_at);
                                const kdy = rv?.created_at || addDetailData.posledni_revize_at || addDetailData.created_at;
                                const kdo = rv?.uzivatel || addDetailData.posledni_revize_uzivatel;
                                const kdoStr = kdo ? `${kdo.jmeno || ''} ${kdo.prijmeni || ''}`.trim() : null;
                                return (
                                    <div className="rounded-xl p-3 flex-shrink-0" style={{ ...cardStyle }}>
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                            <ClipboardList size={13} style={{ color: SECTION_COLOR }} />
                                            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: secondaryColor }}>{jeRevize ? 'Poslední revize' : 'Založeno'}</span>
                                            <button onClick={async () => { if (addDetailTab === 'receptura' && !(await guardAddReceptura())) return; setAddDetailTab('revize'); }}
                                                className="ml-auto text-[10px] font-medium flex items-center gap-0.5" style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>
                                                Historie <ChevronRight size={11} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] min-w-0 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                                            <span className="font-medium flex-shrink-0">{kdy ? fmtDate(kdy) : '—'}</span>
                                            {kdoStr && <span className="truncate" style={{ color: mutedColor }}>· {kdoStr}</span>}
                                            {!jeRevize && <span className="truncate" style={{ color: mutedColor }}>· zatím bez revize</span>}
                                        </div>
                                        {rv?.poznamka && (
                                            <p className="text-[11px] mt-1 line-clamp-2" style={{ color: secondaryColor }}>{rv.poznamka}</p>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    ) : null)}
                />
            )}

            {potvrzeni}

            {/* ═══════ TAB: MAPOVÁNÍ OBJEDNAME (dočasná) ═══════ */}
            {tab === 'objedname' && isAdmin && <MapovaniObjedname />}

            {/* ── Správa skupin přídavků modal ── */}
            {addSkupinyModalOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                    onClick={e => { if (e.target === e.currentTarget) { setAddSkupinyModalOpen(false); setAddSkupinyForm(null); } }}>
                    <div style={{ ...glassCard, width: 600, maxWidth: '95vw', maxHeight: '80vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Skupiny ingrediencí</h3>
                            <button onClick={() => { setAddSkupinyModalOpen(false); setAddSkupinyForm(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>

                        {/* Form */}
                        {addSkupinyForm && (
                            <div className="p-4 space-y-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                <div className="grid grid-cols-2 gap-3">
                                    {renderFormField('Název *', <input value={addSkupinyForm.nazev} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, nazev: e.target.value })} style={inputStyle} autoFocus />)}
                                    {renderFormField('Typ výběru', (
                                        <select value={addSkupinyForm.typ_vyberu} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, typ_vyberu: e.target.value })} style={selectStyle}>
                                            <option value="multi" style={optionStyle}>Multi (více voleb)</option>
                                            <option value="single" style={optionStyle}>Single (jedna volba)</option>
                                        </select>
                                    ))}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {renderFormField('Min výběru', <input type="number" min="0" value={addSkupinyForm.min_vyberu} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, min_vyberu: parseInt(e.target.value) || 0 })} style={inputStyle} />)}
                                    {renderFormField('Max výběru', <input type="number" min="1" value={addSkupinyForm.max_vyberu || ''} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, max_vyberu: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} placeholder="∞" />)}
                                    {renderFormField('Pořadí', <input type="number" min="0" value={addSkupinyForm.poradi} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, poradi: parseInt(e.target.value) || 0 })} style={inputStyle} />)}
                                </div>
                                {/* Množství jedné volby. „Min/max výběru" nahoře říká, kolik RŮZNÝCH
                                    přídavků si host vezme; tohle kolik kusů jednoho z nich. */}
                                {addSkupinyForm.typ_vyberu === 'single' ? (
                                    <p className="text-[10px]" style={{ color: mutedColor }}>
                                        Ve skupině typu single se z nabídky bere právě jedna volba — množství se neuplatní.
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3 items-end">
                                        {renderFormField('Výchozí max kusů', (
                                            <input type="number" min="1" max="99" value={addSkupinyForm.vychozi_max_ks ?? 1}
                                                onChange={e => setAddSkupinyForm({ ...addSkupinyForm, vychozi_max_ks: Math.min(99, Math.max(1, parseInt(e.target.value) || 1)) })}
                                                style={inputStyle} />
                                        ))}
                                        <label className="flex items-center gap-2 text-xs cursor-pointer pb-2" style={{ color: 'var(--text-primary)' }}>
                                            <input type="checkbox" checked={(addSkupinyForm.vychozi_min_ks ?? 0) < 0}
                                                onChange={e => setAddSkupinyForm({ ...addSkupinyForm, vychozi_min_ks: e.target.checked ? -1 : 0 })} />
                                            Jde odebrat (bez ...)
                                        </label>
                                    </div>
                                )}
                                <p className="text-[10px]" style={{ color: mutedColor }}>
                                    Platí pro všechny ingredience skupiny; u konkrétního produktu se dá přebít na záložce Ingredience.
                                    Za odebrání se neplatí — je to pokyn kuchyni, ne prodej.
                                </p>
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                                        <input type="checkbox" checked={addSkupinyForm.povinny} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, povinny: e.target.checked })} /> Povinná
                                    </label>
                                    <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                                        <input type="checkbox" checked={addSkupinyForm.aktivni} onChange={e => setAddSkupinyForm({ ...addSkupinyForm, aktivni: e.target.checked })} /> Aktivní
                                    </label>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={async () => {
                                        setAddSkupinySaving(true);
                                        try {
                                            if (addSkupinyForm.id) {
                                                await api(`/api/recepty/pridavky-skupiny/${addSkupinyForm.id}`, 'PUT', addSkupinyForm);
                                            } else {
                                                await api('/api/recepty/pridavky-skupiny', 'POST', addSkupinyForm);
                                            }
                                            setAddSkupinyForm(null);
                                            loadAddSkupiny();
                                        } catch (e) { alert(e.error || 'Chyba'); }
                                        setAddSkupinySaving(false);
                                    }} disabled={addSkupinySaving || !addSkupinyForm.nazev} style={btnPrimary}>
                                        {addSkupinySaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {addSkupinyForm.id ? 'Uložit' : 'Vytvořit'}
                                    </button>
                                    <button onClick={() => setAddSkupinyForm(null)} style={btnSecondary}>Zrušit</button>
                                </div>
                            </div>
                        )}

                        {/* List + button */}
                        <div className="flex-1 overflow-y-auto">
                            {!addSkupinyForm && (
                                <div className="p-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                                    <button onClick={() => setAddSkupinyForm({ nazev: '', typ_vyberu: 'multi', min_vyberu: 0, max_vyberu: null, povinny: false, poradi: addSkupiny.length, aktivni: true, vychozi_min_ks: 0, vychozi_max_ks: 1 })}
                                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium"
                                        style={{ background: SECTION_BG, color: SECTION_COLOR, border: `1px dashed ${SECTION_COLOR}40`, cursor: 'pointer' }}>
                                        <Plus size={14} /> Nová skupina
                                    </button>
                                </div>
                            )}
                            {addSkupiny.length === 0 ? (
                                <p className="text-xs text-center py-6" style={{ color: mutedColor }}>Žádné skupiny ingrediencí.</p>
                            ) : addSkupiny.map(s => (
                                <div key={s.id} className="flex items-center justify-between px-4 py-3"
                                    style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` }}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{s.nazev}</span>
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: 'rgba(236,72,153,0.12)', color: '#EC4899' }}>{s.typ_vyberu}</span>
                                        <span className="text-[10px]" style={{ color: mutedColor }}>{s.min_vyberu}–{s.max_vyberu ?? '∞'}</span>
                                        <span className="text-[10px]" style={{ color: mutedColor }}>· {popisRozsahuKusu(s.vychozi_min_ks ?? 0, s.vychozi_max_ks ?? 1)}</span>
                                        <span className="text-[10px]" style={{ color: s.povinny ? '#EF4444' : '#22C55E' }}>{s.povinny ? 'povinná' : 'volitelná'}</span>
                                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold" style={{
                                            background: s.aktivni ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                            color: s.aktivni ? '#22C55E' : '#EF4444',
                                        }}>{s.aktivni ? 'Akt' : 'Off'}</span>
                                        {(s.pridavky_count > 0 || s.produkty_count > 0) && (
                                            <span className="text-[10px]" style={{ color: mutedColor }}>{s.pridavky_count || 0} ingrediencí · {s.produkty_count || 0} produktů</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setAddSkupinyForm({ id: s.id, nazev: s.nazev, typ_vyberu: s.typ_vyberu, min_vyberu: s.min_vyberu, max_vyberu: s.max_vyberu, povinny: s.povinny, poradi: s.poradi, aktivni: s.aktivni, vychozi_min_ks: s.vychozi_min_ks ?? 0, vychozi_max_ks: s.vychozi_max_ks ?? 1 })}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR, padding: 4 }}><Edit3 size={14} /></button>
                                        <button onClick={async () => {
                                            if (!(await potvrdit(`Smazat skupinu „${s.nazev}"?`, { potvrdit: 'Smazat' }))) return;
                                            try { await api(`/api/recepty/pridavky-skupiny/${s.id}`, 'DELETE'); loadAddSkupiny(); }
                                            catch (e) { alert(e.error || 'Nelze smazat'); }
                                        }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Výběr ikony / obrázku položky ── */}
            {/* key = přemountování při změně kategorie: bez něj chvíli svítí
                stav předchozí kategorie („Analyzuji…") a mate to. */}
            <AiAnalyzaModal key={aiAnalyzaKat?.id || 'zadna'} open={!!aiAnalyzaKat} kategorie={aiAnalyzaKat}
                onClose={() => setAiAnalyzaKat(null)} isDark={isDark} />

            <IconPickerModal open={iconPickerOpen} onClose={() => setIconPickerOpen(false)} isDark={isDark}
                current={recDetailData}
                onPick={(patch) => {
                    if (!recDetail || recDetail === 'new') return;
                    api(`/api/recepty/recepty/${recDetail}`, 'PATCH', patch)
                        .then(() => { loadRecepty(); loadRecDetail(recDetail, { keepTab: true }); })
                        .catch(e => alert(e.error || 'Chyba při ukládání ikony'));
                }} />

            {/* ── Výběr ikony / obrázku přídavku ── */}
            <IconPickerModal open={addIconPickerOpen} onClose={() => setAddIconPickerOpen(false)} isDark={isDark}
                current={addDetailData}
                onPick={(patch) => {
                    if (!addDetail || addDetail === 'new') return;
                    api(`/api/recepty/recepty/${addDetail}`, 'PATCH', patch)
                        .then(() => { loadAddRecepty({ silent: true }); loadAddDetail(addDetail, { keepTab: true }); })
                        .catch(e => alert(e.error || 'Chyba při ukládání ikony'));
                }} />

            {/* ── Modal: zadání revize při uložení přídavku ── */}
            {addSaveRevModalOpen && (
                <div onClick={() => !addSaving && setAddSaveRevModalOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ ...glassCard, width: 420, maxWidth: '95vw', borderRadius: 12, overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><ClipboardList size={15} style={{ color: SECTION_COLOR }} /> Uložit změny — revize</h3>
                            <button onClick={() => setAddSaveRevModalOpen(false)} disabled={addSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="flex items-center gap-1.5">
                                <Sparkles size={12} style={{ color: '#A855F7' }} />
                                <span className="text-[11px]" style={{ color: mutedColor }}>Návrh dle změn — uprav podle potřeby</span>
                            </div>
                            <textarea value={addRevizePoznamka} onChange={e => setAddRevizePoznamka(e.target.value)} placeholder="Popište provedené změny…" rows={3} autoFocus
                                className="w-full rounded-lg px-3 py-2 text-xs"
                                style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)', border: `1px solid ${!addRevizePoznamka.trim() ? 'rgba(239,68,68,0.3)' : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`, color: 'var(--text-primary)', resize: 'vertical' }} />
                            {addError && <p className="text-xs" style={{ color: '#EF4444' }}>{addError}</p>}
                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={() => setAddSaveRevModalOpen(false)} disabled={addSaving} style={btnSecondary}>Zrušit</button>
                                <button onClick={saveAddWithRevision} disabled={addSaving || !addRevizePoznamka.trim()}
                                    style={{ ...btnPrimary, opacity: (addSaving || !addRevizePoznamka.trim()) ? 0.5 : 1 }}>
                                    {addSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit a zapsat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal: zadání revize při uložení ── */}
            {saveRevModalOpen && (
                <div onClick={() => !recSaving && setSaveRevModalOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ ...glassCard, width: 420, maxWidth: '95vw', borderRadius: 12, overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><ClipboardList size={15} style={{ color: SECTION_COLOR }} /> Uložit změny — revize</h3>
                            <button onClick={() => setSaveRevModalOpen(false)} disabled={recSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="flex items-center gap-1.5">
                                <Sparkles size={12} style={{ color: '#A855F7' }} />
                                <span className="text-[11px]" style={{ color: mutedColor }}>Návrh dle změn — uprav podle potřeby</span>
                            </div>
                            <textarea value={revizePoznamka} onChange={e => setRevizePoznamka(e.target.value)} placeholder="Popište provedené změny…" rows={3} autoFocus
                                className="w-full rounded-lg px-3 py-2 text-xs"
                                style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)', border: `1px solid ${!revizePoznamka.trim() ? 'rgba(239,68,68,0.3)' : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`, color: 'var(--text-primary)', resize: 'vertical' }} />
                                                <label className="flex items-start gap-2 " style={{ cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={revizeSeznameni} onChange={e => setRevizeSeznameni(e.target.checked)}
                                                        style={{ accentColor: SECTION_COLOR, width: 15, height: 15, marginTop: 1, cursor: 'pointer' }} />
                                                    <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                                                        Vyžaduje seznámení obsluhy
                                                        <span className="block text-[10px]" style={{ color: mutedColor }}>Zaměstnanci s recepty v Cockpitu změnu potvrdí. U drobností nezaškrtávej.</span>
                                                    </span>
                                                </label>
                            {recError && <p className="text-xs" style={{ color: '#EF4444' }}>{recError}</p>}
                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={() => setSaveRevModalOpen(false)} disabled={recSaving} style={btnSecondary}>Zrušit</button>
                                <button onClick={saveReceptWithRevision} disabled={recSaving || !revizePoznamka.trim()}
                                    style={{ ...btnPrimary, opacity: (recSaving || !revizePoznamka.trim()) ? 0.5 : 1 }}>
                                    {recSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit a zapsat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PrejmenovatVariantuModal
                open={prejmenovatVariantu}
                onClose={() => setPrejmenovatVariantu(false)}
                isDark={isDark}
                nazevProduktu={recDetailData?.nazev}
                varianta={recDetailData?.varianta || ''}
                onUlozit={renameVariant}
            />

            {/* ── Potvrzení mazání (recept i přídavek) ── */}
            <PotvrzeniModal
                otevreno={!!smazatModal}
                nadpis={smazatModal?.typ === 'pridavek' ? 'Smazat ingredienci' : 'Smazat recept'}
                polozka={smazatModal?.nazev}
                popis="Smazání je nevratné — zmizí i receptura, kroky a historie revizí. Pokud chceš položku jen stáhnout z nabídky, použij Archivovat."
                probiha={smazatBusy}
                isDark={isDark} mutedColor={mutedColor} glassCard={glassCard} btnSecondary={btnSecondary}
                onPotvrdit={potvrditSmazani}
                onZrusit={() => setSmazatModal(null)}
            />

            {/* ── Kde se položka používá (+ hromadná náhrada) ── */}
            {pouzitiModal && (
                <PouzitiModal
                    typ={pouzitiModal.typ}
                    id={pouzitiModal.id}
                    isDark={isDark}
                    onNavigate={onNavigate}
                    onClose={() => setPouzitiModal(null)}
                    onZmena={() => {
                        // Náhrada přepsala kusovníky — seznam i kalkulace jsou po ní jiné.
                        loadRecepty();
                        if (recDetail && recDetail !== 'new') { loadRecDetail(recDetail, { keepTab: true }); refreshKalkulace(); }
                        if (addDetail && addDetail !== 'new') { loadAddDetail(addDetail, { keepTab: true }); refreshAddKalkulace(); }
                    }}
                />
            )}

            {comboSpravaOpen && (
                <ComboSkupinySprava isDark={isDark} onZavrit={() => setComboSpravaOpen(false)} />
            )}

            {/* ── Manažer kategorií modal ── */}
            {katModalOpen && (
                <KategorieManagerModal
                    typ={recTypFilter}
                    strom={recKatStromActive}
                    flatKat={flatRecKat}
                    onReload={() => reloadKatStrom(recTypFilter)}
                    onClose={() => setKatModalOpen(false)}
                    cardStyle={cardStyle} inputStyle={inputStyle} selectStyle={selectStyle} optionStyle={optionStyle}
                    btnPrimary={btnPrimary} btnSecondary={btnSecondary}
                    secondaryColor={secondaryColor} mutedColor={mutedColor} isDark={isDark}
                />
            )}

            {/* ── Media Picker Modal ── */}
            {eshopMediaModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                    onClick={e => { if (e.target === e.currentTarget) setEshopMediaModal(false); }}>
                    <div style={{ ...glassCard, width: 700, maxWidth: '95vw', maxHeight: '80vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Vybrat obrázek z knihovny</h3>
                            <button onClick={() => setEshopMediaModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                            <input placeholder="Hledat obrázek..." value={eshopMediaSearch}
                                onChange={e => {
                                    setEshopMediaSearch(e.target.value);
                                    const q = e.target.value;
                                    api(`/api/media?typ=foto&per_page=48${q ? `&hledat=${encodeURIComponent(q)}` : ''}`)
                                        .then(r => setEshopMediaList(Array.isArray(r) ? r : (r.data || []))).catch(() => {});
                                }}
                                style={{ ...inputStyle, fontSize: 13 }} />
                        </div>
                        <div className="flex-1 overflow-y-auto p-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                            {(eshopMediaList || []).map(m => (
                                <button key={m.id} onClick={() => {
                                    setRecEditData({ ...recEditData, eshop_media_soubor_id: m.id });
                                    setEshopSelectedMedia(m);
                                    ulozEshopFotku(m);
                                    setEshopMediaModal(false);
                                }}
                                    className="rounded-lg overflow-hidden text-left"
                                    style={{ border: `2px solid ${recEditData?.eshop_media_soubor_id === m.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', padding: 0 }}>
                                    <img src={`/storage/${m.thumbnail_cesta || m.cesta}`} alt={m.nazev}
                                        style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                                    <p className="text-[10px] px-1.5 py-1 truncate" style={{ color: mutedColor }}>{m.nazev}</p>
                                </button>
                            ))}
                            {(eshopMediaList || []).length === 0 && (
                                <p className="text-xs text-center py-8" style={{ color: mutedColor, gridColumn: '1 / -1' }}>Žádné obrázky</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

// ─── Tree helpers pro KategorieManagerModal ──────────────
function kmFlattenTree(nodes, depth = 0) {
    const result = [];
    for (const node of nodes) {
        result.push({ ...node, _depth: depth });
        if (node.children?.length) result.push(...kmFlattenTree(node.children, depth + 1));
    }
    return result;
}
function kmFlattenForApi(tree, parentId = null, depth = 0) {
    const result = [];
    tree.forEach((node, i) => {
        result.push({ id: node.id, parent_id: parentId, poradi: i, hloubka: depth });
        if (node.children?.length) result.push(...kmFlattenForApi(node.children, node.id, depth + 1));
    });
    return result;
}
function kmInsertIntoTree(tree, movedId, parentId, index) {
    const clone = (n) => ({ ...n, children: (n.children || []).map(clone) });
    const t = tree.map(clone);
    const mid = Number(movedId), pid = parentId != null ? Number(parentId) : null;
    let extracted = null;
    const extract = (nodes) => { for (let i = 0; i < nodes.length; i++) { if (Number(nodes[i].id) === mid) { extracted = nodes.splice(i, 1)[0]; return true; } if (extract(nodes[i].children || [])) return true; } return false; };
    extract(t);
    if (!extracted) return t;
    if (pid == null) { t.splice(Math.min(index, t.length), 0, extracted); }
    else { const ins = (nodes) => { for (const n of nodes) { if (Number(n.id) === pid) { if (!n.children) n.children = []; n.children.splice(Math.min(index, n.children.length), 0, extracted); return true; } if (ins(n.children || [])) return true; } return false; }; if (!ins(t)) t.splice(Math.min(index, t.length), 0, extracted); }
    return t;
}

// ─── Manažer kategorií — modální okno s D&D + inline editací ──────────
function KategorieManagerModal({ typ, strom, flatKat, onReload, onClose, cardStyle, inputStyle, selectStyle, optionStyle, btnPrimary, btnSecondary, secondaryColor, mutedColor, isDark }) {
    const [expanded, setExpanded] = useState({});
    const [editId, setEditId] = useState(null);
    const [editNazev, setEditNazev] = useState('');
    const [editAktivni, setEditAktivni] = useState(true);
    const [newName, setNewName] = useState('');
    const [newParent, setNewParent] = useState('');
    const [saving, setSaving] = useState(false);
    const [dragState, setDragState] = useState(null);
    const applyDropRef = useRef(null);

    const visibleItems = useMemo(() => {
        const flatten = (nodes, depth = 0) => {
            const result = [];
            for (const node of nodes) {
                result.push({ ...node, _depth: depth });
                if (node.children?.length && expanded[node.id]) result.push(...flatten(node.children, depth + 1));
            }
            return result;
        };
        return flatten(strom);
    }, [strom, expanded]);

    const allFlat = useMemo(() => kmFlattenTree(strom), [strom]);
    const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    // ── Inline edit ──
    const startEdit = (item) => { setEditId(item.id); setEditNazev(item.nazev); setEditAktivni(item.aktivni); };
    const cancelEdit = () => { setEditId(null); setEditNazev(''); };
    const saveEdit = async () => {
        if (!editNazev.trim()) return;
        setSaving(true);
        try { await api(`/api/recepty/kategorie-surovin/${editId}`, 'PUT', { nazev: editNazev.trim(), aktivni: editAktivni }); setEditId(null); onReload(); }
        catch (e) { alert(e.error || 'Chyba'); }
        setSaving(false);
    };

    // ── Add new ──
    const addNew = async () => {
        if (!newName.trim()) return;
        setSaving(true);
        try { await api('/api/recepty/kategorie-surovin', 'POST', { typ, nazev: newName.trim(), aktivni: true, parent_id: newParent || null }); setNewName(''); setNewParent(''); onReload(); }
        catch (e) { alert(e.error || 'Chyba'); }
        setSaving(false);
    };

    const addChild = (parentNode) => {
        setNewParent(String(parentNode.id));
        setExpanded(prev => ({ ...prev, [parentNode.id]: true }));
    };

    const del = async (id) => {
        if (!confirm('Smazat kategorii?')) return;
        try { await api(`/api/recepty/kategorie-surovin/${id}`, 'DELETE'); onReload(); } catch {}
    };

    // ── Drag & Drop ──
    const getDropMode = useCallback((overElement, pointerY) => {
        if (!overElement) return 'below';
        const rect = overElement.getBoundingClientRect();
        const relY = pointerY - rect.top;
        const height = rect.height;
        if (relY < height * 0.3) return 'above';
        if (relY > height * 0.7) return 'below';
        return 'child';
    }, []);

    const handlePointerDown = useCallback((e, nodeId) => {
        if (!e.target.closest('.kat-drag-handle')) return;
        if (editId) return; // don't drag while editing
        e.preventDefault();
        const el = e.target.closest('[data-kat-id]');
        if (!el) return;
        const offsetY = e.clientY - el.getBoundingClientRect().top;
        const clone = el.cloneNode(true);
        clone.style.cssText = `position:fixed;width:${el.offsetWidth}px;z-index:1000;pointer-events:none;opacity:0.9;background:${isDark ? '#2a2a3e' : '#fff'};border:2px solid #F59E0B;border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,0.3);left:${el.getBoundingClientRect().left}px;top:${e.clientY - offsetY}px`;
        document.body.appendChild(clone);
        el.style.opacity = '0.3';
        let lastDropResult = null;
        const onMove = (ev) => {
            clone.style.top = (ev.clientY - offsetY) + 'px';
            const rows = document.querySelectorAll('[data-kat-id]');
            let found = null;
            for (const row of rows) { const rid = parseInt(row.getAttribute('data-kat-id')); if (rid === nodeId) continue; const rect = row.getBoundingClientRect(); if (ev.clientY >= rect.top && ev.clientY <= rect.bottom) { found = { activeId: nodeId, overId: rid, mode: getDropMode(row, ev.clientY) }; break; } }
            lastDropResult = found;
            setDragState(found);
        };
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            clone.remove();
            el.style.opacity = '1';
            setDragState(null);
            if (lastDropResult && lastDropResult.activeId !== lastDropResult.overId) applyDropRef.current?.(lastDropResult.activeId, lastDropResult.overId, lastDropResult.mode);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }, [isDark, getDropMode, editId]);

    const applyDrop = useCallback(async (activeId, overId, mode) => {
        if (activeId === overId) return;
        let newTree;
        if (mode === 'child') {
            newTree = kmInsertIntoTree(strom, activeId, overId, Infinity);
            setExpanded(prev => ({ ...prev, [overId]: true }));
        } else {
            const targetNode = allFlat.find(n => n.id === overId);
            if (!targetNode) return;
            const parentId = targetNode.parent_id || null;
            const findSiblings = (nodes, pid) => { if (!pid) return nodes; for (const n of nodes) { if (n.id === pid) return n.children || []; const f = findSiblings(n.children || [], pid); if (f) return f; } return null; };
            const siblings = findSiblings(strom, parentId) || strom;
            const targetIdx = siblings.findIndex(s => s.id === overId);
            const movedIdx = siblings.findIndex(s => s.id === activeId);
            let insertIdx = mode === 'above' ? targetIdx : targetIdx + 1;
            if (movedIdx >= 0 && movedIdx < insertIdx) insertIdx--;
            newTree = kmInsertIntoTree(strom, activeId, parentId, Math.max(0, insertIdx));
        }
        const polozky = kmFlattenForApi(newTree);
        if (!polozky.length) return;
        try { await api('/api/recepty/kategorie-surovin/reorder', 'POST', { polozky }); onReload(); }
        catch (e) { alert(e.error || 'Chyba při přeřazení'); }
    }, [strom, allFlat, onReload]);
    applyDropRef.current = applyDrop;

    const typLabel = typ === 'SEM' ? 'polotovarů' : 'produktů';
    const dropColor = '#3B82F6';
    // Glass modal — vždy tmavé sklo, přepíšeme barvy
    const gText = 'rgba(255,255,255,0.9)';
    const gMuted = 'rgba(255,255,255,0.35)';
    const gSecondary = 'rgba(255,255,255,0.55)';
    const gBorder = 'rgba(255,255,255,0.08)';
    const gInputStyle = { ...inputStyle, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(255,255,255,0.1)`, color: gText };
    const gSelectStyle = { ...selectStyle, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(255,255,255,0.1)`, color: gText };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', zIndex: Z_NAD_LISTOU, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={onClose}>
            <div className="rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" style={{
                background: isDark
                    ? 'rgba(20,20,35,0.85)'
                    : 'rgba(30,30,40,0.82)',
                backdropFilter: 'blur(40px) saturate(1.2)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.2)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)'}`,
                boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 1px 0 rgba(255,255,255,0.06) inset',
            }}
                onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 pb-3" style={{ borderBottom: `1px solid ${gBorder}` }}>
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold" style={{ color: gText }}>Kategorie {typLabel}</h3>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: gMuted }}><X size={18} /></button>
                    </div>
                    {/* Přidat novou */}
                    <div className="flex gap-2 mt-3">
                        <input placeholder="Nová kategorie..." value={newName} onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addNew()}
                            style={{ ...gInputStyle, fontSize: 12, flex: 1 }} />
                        <select value={newParent} onChange={e => setNewParent(e.target.value)} style={{ ...gSelectStyle, fontSize: 11, maxWidth: 140 }}>
                            <option value="">Kořen</option>
                            {flatKat.map(k => <option key={k.id} value={k.id}>{'— '.repeat(k._depth)}{k.nazev}</option>)}
                        </select>
                        <button onClick={addNew} disabled={!newName.trim() || saving}
                            style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}>
                            <Plus size={14} />
                        </button>
                    </div>
                </div>

                {/* Tree */}
                <div className="overflow-y-auto flex-1 py-1"
                    onPointerDown={(e) => {
                        const row = e.target.closest('[data-kat-id]');
                        if (row && e.target.closest('.kat-drag-handle')) handlePointerDown(e, parseInt(row.getAttribute('data-kat-id')));
                    }}>
                    {visibleItems.length === 0 ? (
                        <p className="text-center text-xs py-8" style={{ color: gMuted }}>Žádné kategorie</p>
                    ) : visibleItems.map(node => {
                        const hasChildren = node.children?.length > 0;
                        const isExpanded = expanded[node.id];
                        const isEditing = editId === node.id;
                        const dropMode = dragState?.overId === node.id ? dragState.mode : null;

                        return (
                            <div key={node.id} style={{ position: 'relative' }}>
                                {dropMode === 'above' && (
                                    <div style={{ position: 'absolute', top: -1, left: 12 + node._depth * 24, right: 8, height: 2, background: dropColor, borderRadius: 1, zIndex: 10 }}>
                                        <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: dropColor }} />
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5 py-2 px-3 group" data-kat-id={node.id}
                                    style={{
                                        paddingLeft: 12 + node._depth * 24,
                                        background: dropMode === 'child' ? 'rgba(59,130,246,0.1)' : 'transparent',
                                        borderBottom: `1px solid ${gBorder}`,
                                        outline: dropMode === 'child' ? `1px dashed ${dropColor}` : undefined,
                                        borderRadius: dropMode === 'child' ? 6 : undefined,
                                    }}>
                                    <div style={{ cursor: 'grab', color: 'rgba(255,255,255,0.15)', touchAction: 'none' }} className="kat-drag-handle">
                                        <GripVertical size={13} />
                                    </div>
                                    {hasChildren ? (
                                        <button onClick={() => toggleExpand(node.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: gSecondary, padding: 0, display: 'flex' }}>
                                            <ChevronRight size={13} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                        </button>
                                    ) : <span style={{ width: 13 }} />}
                                    <FolderTree size={12} style={{ color: gMuted }} />

                                    {isEditing ? (
                                        <>
                                            <input value={editNazev} onChange={e => setEditNazev(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                                autoFocus style={{ ...gInputStyle, fontSize: 12, flex: 1, padding: '3px 8px' }} />
                                            <label className="flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ color: gSecondary, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={editAktivni} onChange={e => setEditAktivni(e.target.checked)} /> Aktivní
                                            </label>
                                            <button onClick={saveEdit} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22C55E', padding: 2 }}>
                                                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                            </button>
                                            <button onClick={cancelEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: gMuted, padding: 2 }}><X size={12} /></button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="flex-1 text-xs truncate" style={{ color: node.aktivni ? gText : gMuted, fontWeight: node._depth === 0 ? 600 : 400 }}>
                                                {node.nazev}
                                            </span>
                                            {!node.aktivni && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: gMuted }}>Neaktivní</span>}
                                            {dropMode === 'child' && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(59,130,246,0.15)', color: dropColor }}>vnořit</span>}
                                            <div className="hidden group-hover:flex gap-1">
                                                <button onClick={() => addChild(node)} title="Přidat podkategorii" style={{ background: 'none', border: 'none', cursor: 'pointer', color: gMuted, padding: 2 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = gText} onMouseLeave={e => e.currentTarget.style.color = gMuted}><FolderPlus size={12} /></button>
                                                <button onClick={() => startEdit(node)} title="Upravit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: gMuted, padding: 2 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = gText} onMouseLeave={e => e.currentTarget.style.color = gMuted}><Edit3 size={12} /></button>
                                                <button onClick={() => del(node.id)} title="Smazat" style={{ background: 'none', border: 'none', cursor: 'pointer', color: gMuted, padding: 2 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#EF4444'} onMouseLeave={e => e.currentTarget.style.color = gMuted}><Trash2 size={12} /></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {dropMode === 'below' && (
                                    <div style={{ position: 'absolute', bottom: -1, left: 12 + node._depth * 24, right: 8, height: 2, background: dropColor, borderRadius: 1, zIndex: 10 }}>
                                        <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: dropColor }} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer hint */}
                <div className="px-5 py-3 text-[10px] flex items-center gap-1.5" style={{ borderTop: `1px solid ${gBorder}`, color: gMuted }}>
                    <GripVertical size={10} /> Přetáhněte pro řazení a vnořování
                </div>
            </div>
        </div>
    );
}
